import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { positionsAPI, transactionsAPI, clientsAPI, withToast } from "@/services/api";
import { usePositionsPL } from "@/services/priceHooks";
import { managerFetch } from "@/services/staffManagerTransport";
import { 
  X, Download, Edit, DollarSign, Minus, Plus, CreditCard,
  ChevronDown, Trash2, Check, Info, Activity, Play, Pause, RotateCcw, Clock, TrendingUp, TrendingDown
} from "lucide-react";
import { toast } from "sonner";
import LeverageSelector, { LeverageBadge } from "@/components/LeverageSelector";

// Helper function to format date in Turkey timezone
const formatTurkeyTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  return date.toLocaleString('en-GB', { 
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Get current Turkey time
const getTurkeyTime = () => {
  return new Date().toLocaleString('en-GB', { 
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

// Available trading terms
const TRADING_TERMS = [
  { id: 1, name: "0-STD SWAPLI", defaultLeverage: 100 },
  { id: 2, name: "0-STD SWAPSIZ", defaultLeverage: 100 },
  { id: 3, name: "1-GOLD-3", defaultLeverage: 200 },
  { id: 4, name: "1-VIP PLUS", defaultLeverage: 500 },
  { id: 5, name: "ZG Default TRY", defaultLeverage: 100 },
  { id: 6, name: "EXT-200 SP", defaultLeverage: 200 },
  { id: 7, name: "EXT-400", defaultLeverage: 400 },
  { id: 8, name: "L1000", defaultLeverage: 1000 },
];

// Position Virtual Price Controller Modal - For admin to set virtual price per position per client
const PositionVirtualPriceModal = ({ position, onClose, onApply }) => {
  const [marketPrice] = useState(position.current_price || position.entry_price);
  const [virtualPrice, setVirtualPrice] = useState(position.virtualPrice || marketPrice);
  const [targetPrice, setTargetPrice] = useState(position.virtualPrice || marketPrice);
  const [duration, setDuration] = useState(5);
  const [isActive, setIsActive] = useState(position.virtualPriceActive || false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [startPrice, setStartPrice] = useState(null);
  const animationRef = useRef(null);

  // Calculate P&L with virtual price
  const calculatePL = (price) => {
    const qty = position.quantity || position.amount || 1;
    if (position.side === 'buy' || position.type === 'BUY') {
      return (price - position.entry_price) * qty;
    } else {
      return (position.entry_price - price) * qty;
    }
  };

  const marketPL = calculatePL(marketPrice);
  const virtualPL = calculatePL(virtualPrice);
  const targetPL = calculatePL(targetPrice);

  // Price movement animation
  useEffect(() => {
    if (isActive && startTime && startPrice !== null) {
      const updatePrice = () => {
        const now = Date.now();
        const elapsed = (now - startTime) / 1000 / 60;
        const progress = Math.min(elapsed / duration, 1);
        
        const easedProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        const newPrice = startPrice + (targetPrice - startPrice) * easedProgress;
        setVirtualPrice(parseFloat(newPrice.toFixed(5)));
        
        const remaining = Math.max(0, duration - elapsed);
        setTimeRemaining(remaining);
        
        if (progress >= 1) {
          setIsActive(false);
          setVirtualPrice(targetPrice);
          setTimeRemaining(0);
          toast.success(`Position ${position.id} reached target price: ${targetPrice}`);
        } else {
          animationRef.current = requestAnimationFrame(updatePrice);
        }
      };
      
      animationRef.current = requestAnimationFrame(updatePrice);
      return () => animationRef.current && cancelAnimationFrame(animationRef.current);
    }
  }, [isActive, startTime, startPrice, targetPrice, duration, position.id]);

  const handleStart = () => {
    if (targetPrice === virtualPrice) {
      toast.error("Target price must be different from current price");
      return;
    }
    setStartTime(Date.now());
    setStartPrice(virtualPrice);
    setIsActive(true);
    setTimeRemaining(duration);
    toast.info(`Starting price movement: ${virtualPrice} → ${targetPrice} over ${duration} min`);
  };

  const handlePause = () => {
    setIsActive(false);
    animationRef.current && cancelAnimationFrame(animationRef.current);
  };

  const handleReset = () => {
    setIsActive(false);
    setVirtualPrice(marketPrice);
    setTargetPrice(marketPrice);
    setTimeRemaining(0);
    animationRef.current && cancelAnimationFrame(animationRef.current);
    toast.info("Reset to market price");
  };

  const handleApply = () => {
    onApply({
      positionId: position.id,
      virtualPrice,
      virtualPriceActive: isActive,
      targetPrice,
      duration
    });
    onClose();
  };

  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[550px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Activity className="text-white" size={16} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">Virtual Price - Position #{position.id?.toString().slice(-6)}</h2>
              <p className="text-white/80 text-xs">{position.symbol} | {position.side?.toUpperCase() || position.type} | Qty: {position.quantity || position.amount}</p>
            </div>
          </div>
          <button className="text-white/80 hover:text-white" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Price Comparison */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-100 rounded-lg p-3 text-center">
              <label className="text-xs text-gray-500 uppercase">Market Price</label>
              <div className="text-lg font-bold text-gray-800">{marketPrice.toFixed(5)}</div>
              <div className={`text-xs ${marketPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                P&L: {marketPL >= 0 ? '+' : ''}{marketPL.toFixed(2)}
              </div>
            </div>
            <div className={`rounded-lg p-3 text-center ${isActive ? 'bg-orange-50 border-2 border-orange-300' : 'bg-blue-50'}`}>
              <label className="text-xs text-gray-500 uppercase flex items-center justify-center gap-1">
                Virtual Price {isActive && <span className="animate-pulse text-orange-500 text-[10px]">LIVE</span>}
              </label>
              <div className={`text-lg font-bold ${virtualPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {virtualPrice.toFixed(5)}
              </div>
              <div className={`text-xs ${virtualPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                P&L: {virtualPL >= 0 ? '+' : ''}{virtualPL.toFixed(2)}
              </div>
            </div>
            <div className="bg-gray-100 rounded-lg p-3 text-center">
              <label className="text-xs text-gray-500 uppercase">Target Price</label>
              <div className={`text-lg font-bold ${targetPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {targetPrice.toFixed(5)}
              </div>
              <div className={`text-xs ${targetPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                P&L: {targetPL >= 0 ? '+' : ''}{targetPL.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {isActive && (
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 flex items-center gap-1"><Clock size={14} /> Time Remaining</span>
                <span className="text-sm font-mono font-bold text-orange-600">{formatTime(timeRemaining)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-orange-500 h-2 rounded-full transition-all" style={{ width: `${((duration - timeRemaining) / duration) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Target Price Input */}
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
              Set Target Price
              {targetPrice > marketPrice && <TrendingUp size={14} className="text-green-500" />}
              {targetPrice < marketPrice && <TrendingDown size={14} className="text-red-500" />}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                className="sirix-form-input flex-1 font-mono"
                value={targetPrice}
                onChange={(e) => setTargetPrice(parseFloat(e.target.value) || 0)}
                step="0.00001"
                disabled={isActive}
              />
              <button className="px-2 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs" 
                onClick={() => setTargetPrice(p => parseFloat((p * 1.01).toFixed(5)))} disabled={isActive}>+1%</button>
              <button className="px-2 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs"
                onClick={() => setTargetPrice(p => parseFloat((p * 0.99).toFixed(5)))} disabled={isActive}>-1%</button>
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1 mb-2"><Clock size={14} /> Duration (minutes)</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[1, 2, 3, 5, 10, 15].map(m => (
                <button key={m} className={`px-3 py-1.5 rounded text-sm ${duration === m ? 'bg-orange-500 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                  onClick={() => setDuration(m)} disabled={isActive}>{m}m</button>
              ))}
              <input type="number" className="sirix-form-input w-16 text-center" value={duration} 
                onChange={e => setDuration(parseInt(e.target.value) || 1)} min="1" max="60" disabled={isActive} />
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-center gap-3 pt-2">
            {!isActive ? (
              <button className="flex items-center gap-2 px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium" onClick={handleStart}>
                <Play size={16} /> Start Movement
              </button>
            ) : (
              <button className="flex items-center gap-2 px-5 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium" onClick={handlePause}>
                <Pause size={16} /> Pause
              </button>
            )}
            <button className="flex items-center gap-2 px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium" onClick={handleReset}>
              <RotateCcw size={16} /> Reset
            </button>
          </div>

          {/* Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-800">
            <strong>Admin Only:</strong> This virtual price affects ONLY this client's position. Other clients with the same instrument will see the real market price.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-3 border-t bg-gray-50">
          <button className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded font-medium" onClick={onClose}>Cancel</button>
          <button className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-medium" onClick={handleApply}>Apply & Close</button>
        </div>
      </div>
    </div>
  );
};

const AccountDetailModal = ({ client, onClose, onUpdate, initialPositionsTab }) => {
  const [activeTab, setActiveTab] = useState("TRADING");
  const [clientData, setClientData] = useState(client);
  const [positions, setPositions] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [closedPositions, setClosedPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [positionsTab, setPositionsTab] = useState(initialPositionsTab || "OPEN");
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingClosedPosition, setEditingClosedPosition] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState("deposit");
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showOpenPositionModal, setShowOpenPositionModal] = useState(false);
  const [showEditTradingModal, setShowEditTradingModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditType, setCreditType] = useState("credit_in"); // "credit_in" or "credit_out"
  const [processingZeroBalance, setProcessingZeroBalance] = useState(false);
  const [showZeroBalanceConfirm, setShowZeroBalanceConfirm] = useState(false);
  const [showRemoveAccountConfirm, setShowRemoveAccountConfirm] = useState(false);
  const [processingRemoveAccount, setProcessingRemoveAccount] = useState(false);
  
  // Virtual Price Control (Admin Only) - per position per client
  const [showVirtualPriceModal, setShowVirtualPriceModal] = useState(false);
  const [selectedPositionForVirtualPrice, setSelectedPositionForVirtualPrice] = useState(null);
  
  // Client trading settings
  const [clientTradingSettings, setClientTradingSettings] = useState({
    tradingTerms: client.trading_terms || "ZG Default TRY",
    marginRequirements: "Trading Terms", // "Trading Terms" or "Custom"
    customLeverage: client.leverage || 100,
    tradability: client.tradability || "Full Trade",
    state: client.is_active !== false ? "Active" : "Inactive"
  });

  // Handle virtual price control for a position
  const handleOpenVirtualPriceControl = (position) => {
    console.log('handleOpenVirtualPriceControl called with:', position);
    setSelectedPositionForVirtualPrice(position);
    setShowVirtualPriceModal(true);
  };

  // Apply virtual price to a position
  const handleApplyVirtualPrice = (priceData) => {
    const { positionId, virtualPrice, virtualPriceActive, targetPrice, duration } = priceData;
    
    // Update position immediately with current virtual price
    setPositions(prev => prev.map(pos => {
      if (pos.id === positionId) {
        const virtualPL = pos.side === 'buy' || pos.type === 'BUY'
          ? (virtualPrice - pos.entry_price) * (pos.quantity || pos.amount || 1)
          : (pos.entry_price - virtualPrice) * (pos.quantity || pos.amount || 1);
        
        return {
          ...pos,
          virtualPrice: virtualPrice,
          virtualPriceActive: virtualPriceActive,
          virtualTargetPrice: targetPrice,
          virtualDuration: duration,
          virtualStartTime: virtualPriceActive ? Date.now() : null,
          virtualStartPrice: virtualPriceActive ? virtualPrice : null,
          current_price: virtualPrice,
          profit_loss: virtualPL
        };
      }
      return pos;
    }));
    
    toast.success(`Virtual price applied to position #${positionId.toString().slice(-6)}`);
  };

  // Continue virtual price animation for all active positions
  useEffect(() => {
    const activeVirtualPositions = positions.filter(p => p.virtualPriceActive);
    if (activeVirtualPositions.length === 0) return;

    const animationInterval = setInterval(() => {
      setPositions(prev => prev.map(pos => {
        if (!pos.virtualPriceActive || !pos.virtualStartTime || !pos.virtualTargetPrice) {
          return pos;
        }

        const now = Date.now();
        const elapsed = (now - pos.virtualStartTime) / 1000 / 60; // minutes
        const duration = pos.virtualDuration || 5;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease-in-out animation
        const easedProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        const startPrice = pos.virtualStartPrice || pos.virtualPrice;
        const newPrice = startPrice + (pos.virtualTargetPrice - startPrice) * easedProgress;
        
        // Calculate new P&L
        const virtualPL = pos.side === 'buy' || pos.type === 'BUY'
          ? (newPrice - pos.entry_price) * (pos.quantity || pos.amount || 1)
          : (pos.entry_price - newPrice) * (pos.quantity || pos.amount || 1);

        // Check if animation is complete
        if (progress >= 1) {
          return {
            ...pos,
            virtualPrice: pos.virtualTargetPrice,
            virtualPriceActive: false,
            current_price: pos.virtualTargetPrice,
            profit_loss: pos.side === 'buy' || pos.type === 'BUY'
              ? (pos.virtualTargetPrice - pos.entry_price) * (pos.quantity || pos.amount || 1)
              : (pos.entry_price - pos.virtualTargetPrice) * (pos.quantity || pos.amount || 1)
          };
        }

        return {
          ...pos,
          virtualPrice: parseFloat(newPrice.toFixed(5)),
          current_price: parseFloat(newPrice.toFixed(5)),
          profit_loss: virtualPL
        };
      }));
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(animationInterval);
  }, [positions.some(p => p.virtualPriceActive)]);

  // Initialize data - fetch from API with mock fallback
  useEffect(() => {
    const fetchData = async () => {
      // Mock data as fallback
      const mockOpenPositions = [
        { id: "295524075", symbol: "XAUUSD", side: "buy", quantity: 100, units: "Oz.", opened_at: "3/18/2026 7:12:00 PM", entry_price: 4884.37, current_price: 4682.49, spread: 16, source: "Broker", swap: 0, commission: 0, leverage: 100 },
        { id: "295524077", symbol: "A1CAP-IST", side: "sell", quantity: 1, units: "$", opened_at: "3/19/2026 11:33:09 AM", entry_price: 15.73, current_price: 15.75, spread: 3, source: "Superweb Web", swap: 0, commission: 0, leverage: 10 },
        { id: "295524078", symbol: "VERUS-IST", side: "buy", quantity: 1, units: "$", opened_at: "3/19/2026 11:34:19 AM", entry_price: 392.76, current_price: 396.00, spread: 76, source: "Superweb Web", swap: 0, commission: 0, leverage: 10 },
        { id: "295524079", symbol: "A1CAP-IST", side: "buy", quantity: 1, units: "$", opened_at: "3/19/2026 11:46:14 AM", entry_price: 15.75, current_price: 15.73, spread: 2, source: "Superweb Web", swap: 0, commission: 0, leverage: 10 },
      ];

      const mockPendingOrders = [
        { id: "295524004", symbol: "AAL", side: "buy_stop", quantity: 1, units: "$", opened_at: "2/12/2026 5:08:02 PM", entry_price: 14.94, current_price: 10.84, spread: 0, source: "Superweb Web", swap: 0, commission: 0, leverage: 10 },
        { id: "295524005", symbol: "EURUSD", side: "sell_limit", quantity: 0.5, units: "Lots", opened_at: "3/19/2026 10:30:00 AM", entry_price: 1.0950, current_price: 1.0876, spread: 2, source: "Broker", swap: 0, commission: 0, leverage: 100 },
      ];

      const mockClosedPositions = [
        { id: "295524069", symbol: "XAUUSD", side: "buy", quantity: 100, units: "Oz.", opened_at: "3/18/2026 2:49:04 PM", entry_price: 4874.70, close_price: 4886.15, closed_at: "3/18/2026 7:01:34 PM", spread: 16, source: "Broker", swap: 0, commission: 0, leverage: 100, profit_loss: 1145.00 },
        { id: "295524068", symbol: "EURUSD", side: "sell", quantity: 50, units: "Lots", opened_at: "3/17/2026 9:30:00 AM", entry_price: 1.0950, close_price: 1.0876, closed_at: "3/18/2026 4:15:00 PM", spread: 2, source: "Broker", swap: -2.50, commission: 5.00, leverage: 100, profit_loss: 370.00 },
        { id: "295524067", symbol: "BTCUSD", side: "buy", quantity: 0.5, units: "BTC", opened_at: "3/16/2026 10:00:00 AM", entry_price: 67500.00, close_price: 68200.00, closed_at: "3/17/2026 3:30:00 PM", spread: 50, source: "Superweb Web", swap: 0, commission: 10.00, leverage: 50, profit_loss: 350.00 },
      ];

      try {
        // Fetch live financials from backend (synced with WebTrader)
        const [liveFinancials, apiTransactions] = await Promise.all([
          clientsAPI.getLiveFinancials(client.id).catch(() => null),
          transactionsAPI.getByClient(client.id).catch(() => null),
        ]);

        // Fetch pending orders from orders API (separate from positions)
        const fetchPendingOrders = async () => {
          try {
            const token = localStorage.getItem('token');
            const response = await managerFetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders?client_id=${client.id}&status=pending`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              return await response.json();
            }
          } catch (e) {
            console.log('Error fetching pending orders:', e);
          }
          return [];
        };
        
        const pendingOrdersFromAPI = await fetchPendingOrders();
        setPendingOrders(pendingOrdersFromAPI.length > 0 ? pendingOrdersFromAPI : mockPendingOrders);

        if (liveFinancials) {
          // Use live positions from backend (already enriched with current prices and P&L)
          const openPos = liveFinancials.positions || [];
          
          // Update client data with live financial calculations
          setClientData(prev => ({
            ...prev,
            balance: liveFinancials.balance,
            credit: liveFinancials.credit,
            equity: liveFinancials.equity,
            free_margin: liveFinancials.free_margin,
            used_margin: liveFinancials.used_margin,
            margin_level: liveFinancials.margin_level,
            open_pl: liveFinancials.open_pl
          }));
          
          setPositions(openPos);
          
          // Also fetch closed positions
          const allPositions = await positionsAPI.getByClient(client.id).catch(() => []);
          const closedPos = allPositions.filter(p => p.status === 'closed');
          setClosedPositions(closedPos);
        } else {
          // Fallback to regular position fetching if live financials fails
          const apiPositions = await positionsAPI.getByClient(client.id).catch(() => null);
          const hasApiPositions = apiPositions && apiPositions.length > 0;
          const openPos = hasApiPositions ? apiPositions.filter(p => p.status === 'open') : mockOpenPositions;
          const closedPos = hasApiPositions ? apiPositions.filter(p => p.status === 'closed') : mockClosedPositions;
          
          const positionsWithPL = openPos.map(p => ({
            ...p,
            profit_loss: calculatePositionPL(p)
          }));

          setPositions(positionsWithPL);
          setClosedPositions(closedPos);
          // Note: pendingOrders already set from the fetchPendingOrders call above
        }
        
        if (apiTransactions && apiTransactions.length > 0) setTransactions(apiTransactions);
        
      } catch (error) {
        console.log('Using mock data due to API error:', error.message);
        // Use mock data on error
        const positionsWithPL = mockOpenPositions.map(p => ({
          ...p,
          profit_loss: calculatePositionPL(p)
        }));
        setPositions(positionsWithPL);
        setPendingOrders(mockPendingOrders);
        setClosedPositions(mockClosedPositions);
      }
    };

    fetchData();
  }, [client.id]);

  // Calculate P&L for a single position
  const calculatePositionPL = (position) => {
    let pl;
    if (position.side === "buy" || position.direction === "buy") {
      pl = (position.current_price - position.entry_price) * (position.quantity || position.volume || 0);
    } else {
      pl = (position.entry_price - position.current_price) * (position.quantity || position.volume || 0);
    }
    
    // Deduct spread cost from P&L (spread is in pips/points, convert to currency)
    // Spread cost = spread * quantity * pip value
    // For simplicity, we assume 1 pip = 0.0001 for forex pairs, 0.01 for others
    const spread = position.spread || 0;
    const quantity = position.quantity || position.volume || 0;
    const pipValue = position.symbol?.includes('USD') && !position.symbol?.includes('XAU') && !position.symbol?.includes('BTC') ? 0.0001 : 0.01;
    const spreadCost = spread * quantity * pipValue;
    
    return pl - spreadCost;
  };

  // Financial Calculations - uses live data from backend when available
  const financialData = useMemo(() => {
    // Use live financial data from backend if available (synced with WebTrader)
    const balance = clientData.balance || 0;
    const credit = clientData.credit || 0;
    
    // Use backend-calculated open P&L if available, otherwise calculate locally
    const openPL = clientData.open_pl !== undefined ? clientData.open_pl : positions.reduce((sum, p) => {
      return sum + (p.profit_loss || 0);
    }, 0);
    
    // Use backend-calculated equity if available
    const equity = clientData.equity !== undefined ? clientData.equity : (balance + credit + openPL);
    
    // Calculate Margin from positions
    const margin = clientData.used_margin !== undefined ? clientData.used_margin : positions.reduce((sum, p) => {
      const positionValue = Math.abs((p.quantity || p.volume || 0) * (p.current_price || p.entry_price || 0));
      const leverage = p.leverage || 100;
      return sum + (positionValue / leverage);
    }, 0);
    
    // Use backend-calculated free margin if available
    const freeMargin = clientData.free_margin !== undefined ? clientData.free_margin : (equity - margin);
    
    // Use backend-calculated margin level if available
    const marginLevel = clientData.margin_level !== undefined ? clientData.margin_level : (margin > 0 ? (equity / margin) * 100 : 0);
    
    // Exposure = Sum of all position values
    const exposure = positions.reduce((sum, p) => sum + Math.abs((p.quantity || p.volume || 0) * (p.current_price || p.entry_price || 0)), 0);
    
    return {
      balance,
      credit,
      equity,
      openPL,
      margin,
      freeMargin,
      marginLevel,
      exposure
    };
  }, [clientData.balance, clientData.credit, clientData.equity, clientData.open_pl, clientData.free_margin, clientData.used_margin, clientData.margin_level, positions]);

  // Position Selection
  const handleSelectPosition = (positionId) => {
    setSelectedPositions(prev => 
      prev.includes(positionId) 
        ? prev.filter(id => id !== positionId)
        : [...prev, positionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPositions.length === positions.length) {
      setSelectedPositions([]);
    } else {
      setSelectedPositions(positions.map(p => p.id));
    }
  };

  // Edit Position - opens modal for single selected position
  const handleEditClick = () => {
    if (selectedPositions.length === 0) {
      toast.error("Please select a position to edit");
      return;
    }
    if (selectedPositions.length === 1) {
      const position = positions.find(p => p.id === selectedPositions[0]);
      if (position) {
        setEditingPosition(position);
      }
    } else {
      // Multiple positions selected - check if same instrument
      const selectedPos = positions.filter(p => selectedPositions.includes(p.id));
      const instruments = [...new Set(selectedPos.map(p => p.symbol))];
      
      if (instruments.length > 1) {
        toast.error("Bulk edit only works for positions with the same instrument");
        return;
      }
      setShowBulkEditModal(true);
    }
  };

  // Close single position
  const handleCloseClick = () => {
    if (selectedPositions.length === 0) {
      toast.error("Please select a position to close");
      return;
    }
    if (selectedPositions.length === 1) {
      handleClosePosition(selectedPositions[0]);
    } else {
      handleCloseSelected();
    }
  };

  // Close Position
  const handleClosePosition = async (positionId) => {
    const position = positions.find(p => p.id === positionId);
    if (!position) return;

    const pl = calculatePositionPL(position);
    
    if (window.confirm(`Close position ${positionId}?\n${position.symbol} ${position.side} ${position.quantity}\nP&L: ${pl >= 0 ? '+' : ''}$${pl.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`)) {
      try {
        // Call API to close position
        await positionsAPI.close(positionId, { 
          close_price: position.current_price,
          profit_loss: pl 
        });
        
        // Update client balance with P&L
        const newBalance = (clientData.balance || 100000000) + pl;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        
        // Move to closed positions
        setClosedPositions(prev => [...prev, { 
          ...position, 
          closed_at: new Date().toISOString(), 
          close_price: position.current_price,
          profit_loss: pl
        }]);
        
        // Remove from open positions
        setPositions(prev => prev.filter(p => p.id !== positionId));
        setSelectedPositions(prev => prev.filter(id => id !== positionId));
        
        toast.success(`Position closed. P&L: ${pl >= 0 ? '+' : ''}$${pl.toLocaleString()}`);
      } catch (error) {
        console.error('API close failed, updating locally:', error);
        // Fallback to local update
        const newBalance = (clientData.balance || 100000000) + pl;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        setClosedPositions(prev => [...prev, { 
          ...position, 
          closed_at: new Date().toISOString(), 
          close_price: position.current_price,
          profit_loss: pl
        }]);
        setPositions(prev => prev.filter(p => p.id !== positionId));
        setSelectedPositions(prev => prev.filter(id => id !== positionId));
        toast.success(`Position closed locally. P&L: ${pl >= 0 ? '+' : ''}$${pl.toLocaleString()}`);
      }
    }
  };

  const handleCloseSelected = async () => {
    if (selectedPositions.length === 0) {
      toast.error("No positions selected");
      return;
    }
    
    let totalPL = 0;
    selectedPositions.forEach(posId => {
      const pos = positions.find(p => p.id === posId);
      if (pos) totalPL += calculatePositionPL(pos);
    });
    
    if (window.confirm(`Close ${selectedPositions.length} position(s)?\nTotal P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`)) {
      try {
        // Call API to close each selected position
        const closePromises = selectedPositions.map(posId => {
          const pos = positions.find(p => p.id === posId);
          return positionsAPI.close(posId, {
            close_price: pos.current_price,
            profit_loss: calculatePositionPL(pos)
          }).catch(err => ({ error: err, positionId: posId }));
        });
        
        await Promise.all(closePromises);
        
        const newBalance = (clientData.balance || 100000000) + totalPL;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        
        // Move all selected to closed
        const closedOnes = positions.filter(p => selectedPositions.includes(p.id)).map(p => ({
          ...p,
          closed_at: new Date().toISOString(),
          close_price: p.current_price,
          profit_loss: calculatePositionPL(p)
        }));
        
        setClosedPositions(prev => [...prev, ...closedOnes]);
        setPositions(prev => prev.filter(p => !selectedPositions.includes(p.id)));
        setSelectedPositions([]);
        
        toast.success(`${closedOnes.length} positions closed. Total P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`);
      } catch (error) {
        console.error('Close positions API failed:', error);
        // Fallback to local update
        const newBalance = (clientData.balance || 100000000) + totalPL;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        const closedOnes = positions.filter(p => selectedPositions.includes(p.id)).map(p => ({
          ...p,
          closed_at: new Date().toISOString(),
          close_price: p.current_price,
          profit_loss: calculatePositionPL(p)
        }));
        setClosedPositions(prev => [...prev, ...closedOnes]);
        setPositions(prev => prev.filter(p => !selectedPositions.includes(p.id)));
        setSelectedPositions([]);
        toast.success(`Positions closed locally. Total P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`);
      }
    }
  };

  const handleCloseAll = async () => {
    if (positions.length === 0) {
      toast.error("No open positions");
      return;
    }
    
    const totalPL = positions.reduce((sum, p) => sum + calculatePositionPL(p), 0);
    
    if (window.confirm(`Close ALL ${positions.length} position(s)?\nTotal P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`)) {
      try {
        // Call API to close all positions
        const closePromises = positions.map(pos => 
          positionsAPI.close(pos.id, {
            close_price: pos.current_price,
            profit_loss: calculatePositionPL(pos)
          }).catch(err => ({ error: err, positionId: pos.id }))
        );
        
        await Promise.all(closePromises);
        
        const newBalance = (clientData.balance || 100000000) + totalPL;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        
        const closedOnes = positions.map(p => ({
          ...p,
          closed_at: new Date().toISOString(),
          close_price: p.current_price,
          profit_loss: calculatePositionPL(p)
        }));
        
        setClosedPositions(prev => [...prev, ...closedOnes]);
        setPositions([]);
        setSelectedPositions([]);
        
        toast.success(`All positions closed. Total P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`);
      } catch (error) {
        console.error('Close all API failed:', error);
        // Fallback to local
        const newBalance = (clientData.balance || 100000000) + totalPL;
        setClientData(prev => ({ ...prev, balance: newBalance }));
        const closedOnes = positions.map(p => ({
          ...p,
          closed_at: new Date().toISOString(),
          close_price: p.current_price,
          profit_loss: calculatePositionPL(p)
        }));
        setClosedPositions(prev => [...prev, ...closedOnes]);
        setPositions([]);
        setSelectedPositions([]);
        toast.success(`All positions closed locally. Total P&L: ${totalPL >= 0 ? '+' : ''}$${totalPL.toLocaleString()}`);
      }
    }
  };

  // Update Position (from edit modal) - THIS UPDATES STATE AND RECALCULATES
  const handleUpdatePosition = async (updatedPositionOrId, updates = null) => {
    try {
      // Handle two call patterns:
      // 1. handleUpdatePosition(positionObject) - from edit position modal
      // 2. handleUpdatePosition(positionId, {field: value}) - from inline editing
      
      let positionId, positionUpdates, fullPosition;
      
      if (updates !== null) {
        // Pattern 2: inline editing (e.g., spread)
        positionId = updatedPositionOrId;
        positionUpdates = updates;
        fullPosition = positions.find(p => p.id === positionId);
        if (fullPosition) {
          fullPosition = { ...fullPosition, ...updates };
        }
      } else {
        // Pattern 1: full position update from modal
        fullPosition = updatedPositionOrId;
        positionId = fullPosition.id;
        positionUpdates = {
          entry_price: fullPosition.entry_price,
          quantity: fullPosition.quantity,
          leverage: fullPosition.leverage,
          stop_loss: fullPosition.stop_loss,
          take_profit: fullPosition.take_profit,
          spread: fullPosition.spread
        };
      }
      
      // Call API to update position
      await positionsAPI.update(positionId, positionUpdates);
      
      // Calculate new P&L with updated data
      const newPL = calculatePositionPL(fullPosition);
      
      setPositions(prev => prev.map(p => 
        p.id === positionId 
          ? { ...p, ...positionUpdates, profit_loss: newPL }
          : p
      ));
      
      if (updates === null) {
        setEditingPosition(null);
      }
      
      const updateType = updates?.spread !== undefined ? `Spread updated to ${updates.spread}` : "Position updated";
      toast.success(`${updateType} - Financials recalculated`);
    } catch (error) {
      console.error('Position update API failed:', error);
      // Still update locally as fallback
      const updatedPosition = updates !== null 
        ? { ...positions.find(p => p.id === updatedPositionOrId), ...updates }
        : updatedPositionOrId;
      const newPL = calculatePositionPL(updatedPosition);
      setPositions(prev => prev.map(p => 
        p.id === (updates !== null ? updatedPositionOrId : updatedPosition.id)
          ? { ...p, ...(updates || updatedPosition), profit_loss: newPL }
          : p
      ));
      if (updates === null) {
        setEditingPosition(null);
      }
      toast.success("Position updated locally");
    }
  };

  // Bulk Update Positions
  const handleBulkUpdate = async (updatedPositions) => {
    try {
      // Call API for each position update
      const updatePromises = updatedPositions.map(pos => 
        positionsAPI.update(pos.id, {
          entry_price: pos.entry_price,
          quantity: pos.quantity,
          leverage: pos.leverage
        }).catch(err => ({ error: err, position: pos }))
      );
      
      await Promise.all(updatePromises);
      
      setPositions(prev => {
        const newPositions = [...prev];
        updatedPositions.forEach(updated => {
          const index = newPositions.findIndex(p => p.id === updated.id);
          if (index !== -1) {
            newPositions[index] = { ...updated, profit_loss: calculatePositionPL(updated) };
          }
        });
        return newPositions;
      });
      
      setShowBulkEditModal(false);
      setSelectedPositions([]);
      toast.success(`${updatedPositions.length} positions updated - Financials recalculated`);
    } catch (error) {
      console.error('Bulk update API failed:', error);
      // Fallback to local update
      setPositions(prev => {
        const newPositions = [...prev];
        updatedPositions.forEach(updated => {
          const index = newPositions.findIndex(p => p.id === updated.id);
          if (index !== -1) {
            newPositions[index] = { ...updated, profit_loss: calculatePositionPL(updated) };
          }
        });
        return newPositions;
      });
      setShowBulkEditModal(false);
      setSelectedPositions([]);
      toast.success(`${updatedPositions.length} positions updated locally`);
    }
  };

  // Handle Deposit/Withdraw
  const handleTransactionSave = async (amount, txType) => {
    try {
      // Call API to create transaction
      const txData = {
        client_id: client.id,
        type: txType,
        amount: amount,
        source: "Broker",
        comment: "Manual transaction"
      };
      
      const result = await transactionsAPI.create(txData);
      
      // Update local state
      const newBalance = txType === "deposit" 
        ? (clientData.balance || 100000000) + amount 
        : (clientData.balance || 100000000) - amount;
      
      setClientData(prev => ({ ...prev, balance: newBalance }));
      
      // Add to transactions list
      const newTx = {
        id: result?.id || `tx_${Date.now()}`,
        time: getTurkeyTime(),
        type: txType === "deposit" ? "Deposit" : "Withdrawal",
        source: "Broker",
        source_id: "Manual",
        funds_in: txType === "deposit" ? amount : null,
        funds_out: txType === "withdrawal" ? amount : null,
        comment: "",
        sub_type: "Manual"
      };
      setTransactions(prev => [newTx, ...prev]);
      
      setShowTransactionModal(false);
      toast.success(`${txType === "deposit" ? "Deposit" : "Withdrawal"} of $${amount.toLocaleString()} processed`);
    } catch (error) {
      console.error('Transaction API failed, updating locally:', error);
      // Fallback to local update
      const newBalance = txType === "deposit" 
        ? (clientData.balance || 100000000) + amount 
        : (clientData.balance || 100000000) - amount;
      
      setClientData(prev => ({ ...prev, balance: newBalance }));
      
      const newTx = {
        id: `tx_${Date.now()}`,
        time: getTurkeyTime(),
        type: txType === "deposit" ? "Deposit" : "Withdrawal",
        source: "Broker",
        source_id: "Manual",
        funds_in: txType === "deposit" ? amount : null,
        funds_out: txType === "withdrawal" ? amount : null,
        comment: "",
        sub_type: "Manual"
      };
      setTransactions(prev => [newTx, ...prev]);
      
      setShowTransactionModal(false);
      toast.success(`${txType === "deposit" ? "Deposit" : "Withdrawal"} processed locally`);
    }
  };

  // Handle Credit In/Out
  const handleCreditSave = async (amount, type) => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await managerFetch(`${API_BASE}/api/manager/customers/${client.id}/credit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: amount,
          account_id: client.trade_accounts?.[0]?.id || client.id,
          credit_type: type,
          notes: `Manual ${type === 'credit_in' ? 'Credit In' : 'Credit Out'}`
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to process credit');
      }
      
      const result = await response.json();
      
      // Update local state
      const creditChange = type === 'credit_in' ? amount : -amount;
      setClientData(prev => ({ 
        ...prev, 
        credit: (prev.credit || 0) + creditChange,
        equity: (prev.equity || prev.balance || 0) + creditChange
      }));
      
      // Add to transactions list
      const newTx = {
        id: result?.id || `credit_${Date.now()}`,
        time: getTurkeyTime(),
        type: type === 'credit_in' ? 'Credit In' : 'Credit Out',
        source: "Broker",
        source_id: "Manual",
        funds_in: type === 'credit_in' ? amount : null,
        funds_out: type === 'credit_out' ? amount : null,
        comment: "Credit operation",
        sub_type: "Manual"
      };
      setTransactions(prev => [newTx, ...prev]);
      
      setShowCreditModal(false);
      toast.success(`${type === 'credit_in' ? 'Credit In' : 'Credit Out'} of $${amount.toLocaleString()} processed`);
    } catch (error) {
      console.error('Credit operation failed:', error);
      toast.error(error.message || 'Failed to process credit operation');
    }
  };

  // Handle Zero Balance - Show confirmation modal first
  const handleZeroBalanceClick = () => {
    // First check if there are open positions
    if (positions.length > 0) {
      toast.error(`Cannot zero balance: Account has ${positions.length} open position(s). Close all positions first.`);
      return;
    }
    // Show confirmation modal
    setShowZeroBalanceConfirm(true);
  };

  // Execute Zero Balance after confirmation
  const executeZeroBalance = async () => {
    setShowZeroBalanceConfirm(false);
    setProcessingZeroBalance(true);
    
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await managerFetch(`${API_BASE}/api/manager/customers/${client.id}/zero-balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          account_id: client.trade_accounts?.[0]?.id || client.id,
          notes: "Balance zeroed by admin"
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to zero balance');
      }
      
      const result = await response.json();
      
      // Update local state
      setClientData(prev => ({ 
        ...prev, 
        balance: 0,
        credit: 0,
        equity: 0,
        free_margin: 0
      }));
      
      // Add to transactions list
      const newTx = {
        id: result?.transaction?.id || `zero_${Date.now()}`,
        time: getTurkeyTime(),
        type: 'Zero Balance',
        source: "Broker",
        source_id: "Admin",
        funds_in: null,
        funds_out: result.previous_balance + result.previous_credit,
        comment: `Previous Balance: $${result.previous_balance?.toLocaleString()}, Credit: $${result.previous_credit?.toLocaleString()}`,
        sub_type: "Manual"
      };
      setTransactions(prev => [newTx, ...prev]);
      
      toast.success(`Balance and credit zeroed successfully. Previous balance: $${result.previous_balance?.toLocaleString()}, Credit: $${result.previous_credit?.toLocaleString()}`);
    } catch (error) {
      console.error('Zero balance failed:', error);
      toast.error(error.message || 'Failed to zero balance');
    } finally {
      setProcessingZeroBalance(false);
    }
  };

  // Handle Remove Account - Show confirmation modal first
  const handleRemoveAccountClick = () => {
    setShowRemoveAccountConfirm(true);
  };

  // Execute Remove Account after confirmation
  const executeRemoveAccount = async (force = false) => {
    setShowRemoveAccountConfirm(false);
    setProcessingRemoveAccount(true);
    
    try {
      await clientsAPI.delete(client.id, force);
      toast.success("Account removed successfully");
      onClose();
      // Trigger refresh of accounts list
      onUpdate && onUpdate(null, 'deleted');
    } catch (error) {
      console.error('Remove account failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to remove account';
      if (errorMsg.includes('open position') && !force) {
        // Show option to force delete
        if (window.confirm(`${errorMsg}\n\nDo you want to force delete and close all positions?`)) {
          executeRemoveAccount(true);
          return;
        }
      }
      toast.error(errorMsg);
    } finally {
      setProcessingRemoveAccount(false);
    }
  };

  const handleUpdateClient = async (updates) => {
    try {
      const result = await clientsAPI.update(client.id, updates);
      setClientData(result);
      onUpdate && onUpdate(result);
      toast.success("Client updated successfully");
    } catch (error) {
      console.error('Update failed:', error);
      toast.error("Failed to update client");
    }
  };

  // Mock transactions
  const mockTransactions = [
    { id: "295524071", time: "3/18/2026 7:01:56 PM", type: "Deposit", source: "Broker", source_id: "321552", funds_in: 100000000.00, funds_out: null, comment: "", sub_type: "Manual" },
    { id: "295524070", time: "3/18/2026 7:01:42 PM", type: "Withdrawal", source: "Broker", source_id: "321552", funds_in: null, funds_out: 12123455.79, comment: "", sub_type: "Manual" },
  ];

  const displayTransactions = transactions.length > 0 ? transactions : mockTransactions;

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ maxWidth: 1400 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-[#00BCD4]">
          <div className="flex items-center gap-4">
            <div className="bg-white text-[#00BCD4] px-3 py-1 font-bold rounded">
              SirixBroker
            </div>
            <div className="text-white">
              <span className="text-lg">Account {clientData.account_id || clientData.id?.slice(0, 8)} - {clientData.full_name}</span>
              <div className="text-sm opacity-80">Currency: USD ✏️</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["TRADING", "PERSONAL DETAILS", "SECURITY"].map((tab) => (
              <button
                key={tab}
                className={`px-6 py-2 font-medium transition-all ${
                  activeTab === tab 
                    ? "bg-[#00BCD4] text-white border-2 border-white" 
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
            <button onClick={onClose} className="ml-4 text-white hover:bg-white/20 p-1 rounded">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-0 max-h-[75vh] overflow-y-auto">
          {activeTab === "TRADING" && (
            <TradingTab 
              client={clientData}
              positions={positions}
              pendingOrders={pendingOrders}
              closedPositions={closedPositions}
              transactions={displayTransactions}
              positionsTab={positionsTab}
              setPositionsTab={setPositionsTab}
              selectedPositions={selectedPositions}
              onSelectPosition={handleSelectPosition}
              onSelectAll={handleSelectAll}
              onEditPosition={setEditingPosition}
              onEditClick={handleEditClick}
              onEditClosedPosition={setEditingClosedPosition}
              onClosePosition={handleClosePosition}
              onCloseClick={handleCloseClick}
              onCloseSelected={handleCloseSelected}
              onCloseAll={handleCloseAll}
              onOpenPosition={() => setShowOpenPositionModal(true)}
              onAddTransaction={(type) => {
                setTransactionType(type);
                setShowTransactionModal(true);
              }}
              onCreditIn={() => { setCreditType("credit_in"); setShowCreditModal(true); }}
              onCreditOut={() => { setCreditType("credit_out"); setShowCreditModal(true); }}
              onZeroBalance={handleZeroBalanceClick}
              processingZeroBalance={processingZeroBalance}
              financialData={financialData}
              calculatePositionPL={calculatePositionPL}
              onUpdatePosition={handleUpdatePosition}
              clientTradingSettings={clientTradingSettings}
              onEditTrading={() => setShowEditTradingModal(true)}
              onVirtualPrice={handleOpenVirtualPriceControl}
            />
          )}
          {activeTab === "PERSONAL DETAILS" && (
            <PersonalDetailsTab client={clientData} onUpdate={handleUpdateClient} />
          )}
          {activeTab === "SECURITY" && (
            <SecurityTab client={clientData} onUpdate={handleUpdateClient} />
          )}
        </div>

        {/* Footer */}
        <div className="sirix-modal-footer flex justify-between">
          <div>
            <button 
              className="sirix-btn bg-red-600 hover:bg-red-700 text-white"
              onClick={handleRemoveAccountClick}
              disabled={processingRemoveAccount}
              data-testid="remove-account-btn"
            >
              <Trash2 size={14} className="inline mr-1" />
              {processingRemoveAccount ? "Removing..." : "Remove Account"}
            </button>
          </div>
          <div className="flex gap-2">
            <button className="sirix-btn sirix-btn-primary" onClick={onClose}>
              ✓ OK
            </button>
            <button className="sirix-btn sirix-btn-danger" onClick={onClose}>
              ✗ Cancel
            </button>
          </div>
        </div>

        {/* Edit Position Modal */}
        {editingPosition && (
          <EditPositionModal
            position={editingPosition}
            client={clientData}
            financialData={financialData}
            allPositions={positions}
            onClose={() => setEditingPosition(null)}
            onSave={handleUpdatePosition}
          />
        )}

        {/* Bulk Edit Modal */}
        {showBulkEditModal && (
          <BulkEditModal
            positions={positions.filter(p => selectedPositions.includes(p.id))}
            client={clientData}
            financialData={financialData}
            onClose={() => setShowBulkEditModal(false)}
            onSave={handleBulkUpdate}
          />
        )}

        {/* Edit Closed Position Modal */}
        {editingClosedPosition && (
          <EditClosedPositionModal
            position={editingClosedPosition}
            client={clientData}
            financialData={financialData}
            onClose={() => setEditingClosedPosition(null)}
            onSave={(updated) => {
              // Update the closed position
              setClosedPositions(prev => prev.map(p => 
                p.id === updated.id ? updated : p
              ));
              // Update client balance if P&L changed
              const oldPL = editingClosedPosition.profit_loss || 0;
              const newPL = updated.profit_loss || 0;
              const balanceDiff = newPL - oldPL;
              if (balanceDiff !== 0) {
                setClientData(prev => ({ ...prev, balance: (prev.balance || 100000000) + balanceDiff }));
              }
              setEditingClosedPosition(null);
              toast.success("Closed position updated - Balance recalculated");
            }}
            onReopen={(position) => {
              // Move from closed to open positions
              const reopenedPosition = {
                ...position,
                current_price: position.close_price || position.entry_price,
                closed_at: undefined,
                close_price: undefined
              };
              setPositions(prev => [...prev, reopenedPosition]);
              setClosedPositions(prev => prev.filter(p => p.id !== position.id));
              // Subtract the realized P&L from balance (since it's being reopened)
              const pl = position.profit_loss || 0;
              setClientData(prev => ({ ...prev, balance: (prev.balance || 100000000) - pl }));
              setEditingClosedPosition(null);
              toast.success("Position reopened");
            }}
          />
        )}

        {/* Add Transaction Modal */}
        {showTransactionModal && (
          <AddTransactionModal
            client={clientData}
            type={transactionType}
            onClose={() => setShowTransactionModal(false)}
            onSave={handleTransactionSave}
          />
        )}

        {/* Credit In/Out Modal */}
        {showCreditModal && (
          <CreditModal
            client={clientData}
            type={creditType}
            onClose={() => setShowCreditModal(false)}
            onSave={handleCreditSave}
          />
        )}

        {/* Zero Balance Confirmation Modal */}
        {showZeroBalanceConfirm && (
          <ZeroBalanceConfirmModal
            client={clientData}
            financialData={financialData}
            onClose={() => setShowZeroBalanceConfirm(false)}
            onConfirm={executeZeroBalance}
            processing={processingZeroBalance}
          />
        )}

        {/* Remove Account Confirmation Modal */}
        {showRemoveAccountConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowRemoveAccountConfirm(false)}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Trash2 size={20} />
                  Remove Account
                </h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  Are you sure you want to permanently remove this account?
                </p>
                <div className="bg-gray-100 rounded p-3 mb-4">
                  <p className="font-mono text-sm"><strong>Account:</strong> {clientData.account_id}</p>
                  <p className="font-mono text-sm"><strong>Name:</strong> {clientData.full_name}</p>
                  <p className="font-mono text-sm"><strong>Email:</strong> {clientData.email}</p>
                  <p className="font-mono text-sm"><strong>Balance:</strong> ${financialData.balance?.toLocaleString()}</p>
                  {positions.length > 0 && (
                    <p className="font-mono text-sm text-red-600"><strong>Open Positions:</strong> {positions.length}</p>
                  )}
                </div>
                <p className="text-red-600 text-sm mb-4">
                  ⚠️ This action cannot be undone. All data including positions, transactions, and documents will be deleted.
                </p>
              </div>
              <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
                <button 
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                  onClick={() => setShowRemoveAccountConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-2"
                  onClick={() => executeRemoveAccount(false)}
                  disabled={processingRemoveAccount}
                  data-testid="confirm-remove-account-btn"
                >
                  <Trash2 size={16} />
                  {processingRemoveAccount ? "Removing..." : "Remove Account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Open Position Modal */}
        {showOpenPositionModal && (
          <OpenPositionModal
            client={clientData}
            financialData={financialData}
            onClose={() => setShowOpenPositionModal(false)}
            onSave={(newPosition) => {
              // Add the new position with calculated P&L
              const pl = newPosition.side === 'buy'
                ? (newPosition.current_price - newPosition.entry_price) * newPosition.quantity
                : (newPosition.entry_price - newPosition.current_price) * newPosition.quantity;
              
              setPositions(prev => [...prev, { ...newPosition, profit_loss: pl }]);
              setShowOpenPositionModal(false);
              toast.success(`Position opened: ${newPosition.side.toUpperCase()} ${newPosition.quantity} ${newPosition.symbol}`);
            }}
          />
        )}

        {/* Edit Trading Modal */}
        {showEditTradingModal && (
          <EditTradingModal
            client={clientData}
            settings={clientTradingSettings}
            onClose={() => setShowEditTradingModal(false)}
            onSave={(newSettings) => {
              setClientTradingSettings(newSettings);
              setShowEditTradingModal(false);
              toast.success("Trading settings updated");
              
              // If using custom leverage, update client data
              if (newSettings.marginRequirements === "Custom") {
                setClientData(prev => ({
                  ...prev,
                  leverage: newSettings.customLeverage,
                  trading_terms: newSettings.tradingTerms
                }));
              }
            }}
          />
        )}

        {/* Position Virtual Price Modal */}
        {showVirtualPriceModal && selectedPositionForVirtualPrice && (
          <PositionVirtualPriceModal
            position={selectedPositionForVirtualPrice}
            onClose={() => {
              setShowVirtualPriceModal(false);
              setSelectedPositionForVirtualPrice(null);
            }}
            onApply={handleApplyVirtualPrice}
          />
        )}
      </div>
    </div>
  );
};

// TRADING TAB
const TradingTab = ({ 
  client, positions, pendingOrders, closedPositions, transactions, 
  positionsTab, setPositionsTab, selectedPositions, onSelectPosition, 
  onSelectAll, onEditPosition, onEditClick, onEditClosedPosition, onClosePosition,
  onCloseClick, onCloseSelected, onCloseAll, onOpenPosition, onAddTransaction, 
  onCreditIn, onCreditOut, onZeroBalance, processingZeroBalance,
  financialData, calculatePositionPL, onUpdatePosition,
  clientTradingSettings, onEditTrading, onVirtualPrice, tableContent, financialActions, onExport
}) => {
  const [editingSpread, setEditingSpread] = useState(null); // position id being edited
  const [spreadValue, setSpreadValue] = useState('');
  
  const handleSpreadEdit = (pos) => {
    setEditingSpread(pos.id);
    setSpreadValue(pos.spread || 0);
  };
  
  const handleSpreadSave = async (positionId) => {
    const newSpread = parseFloat(spreadValue) || 0;
    if (onUpdatePosition) {
      await onUpdatePosition(positionId, { spread: newSpread });
    }
    setEditingSpread(null);
  };
  
  const handleSpreadKeyDown = (e, positionId) => {
    if (e.key === 'Enter') {
      handleSpreadSave(positionId);
    } else if (e.key === 'Escape') {
      setEditingSpread(null);
    }
  };

  const formatCurrency = (value) => {
    if (value < 0) return `-$${Math.abs(value).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
    return `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  return (
    <div>
      {/* Trading Section */}
      <div className="border-b">
        <div className="sirix-section-header flex justify-between items-center">
          <span>İşlem</span>
          <div className="flex gap-2">
            <button className="sirix-btn sirix-btn-secondary text-xs" data-testid="account-export" onClick={onExport} disabled={!onExport}>
              <Download size={12} /> Dışa Aktar
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs"
              onClick={onEditTrading}
              disabled={!onEditTrading}
              data-testid="edit-trading-btn"
            >
              <Edit size={12} /> İşlem Ayarları
            </button>
          </div>
        </div>
        <div className="p-4 grid grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-gray-500">Durum:</label>
            <div data-testid="account-trading-state" className="border rounded px-3 py-1 bg-gray-50">{{Active:'Aktif',Inactive:'Pasif','Read Only':'Salt Okunur'}[clientTradingSettings.state]||clientTradingSettings.state}</div>
          </div>
          <div>
            <label className="text-xs text-gray-500">İşlem Koşulları:</label>
            <div data-testid="account-trading-term-name" className="border rounded px-3 py-1 bg-gray-50 text-sm">{clientTradingSettings.tradingTerms}</div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Kaldıraç:</label>
            <div className="flex items-center gap-1 text-sm">
              {clientTradingSettings.marginRequirements === "Trading Terms" ? (
                <span className="flex items-center gap-1 text-[#00BCD4]">
                  <Info size={14} /><span data-testid="account-effective-term-leverage">İşlem Koşulu · 1:{clientTradingSettings.customLeverage}</span>
                </span>
              ) : (
                <LeverageBadge value={clientTradingSettings.customLeverage} />
              )}
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">İşlem Yetkisi:</label>
            <div data-testid="account-trading-permission" className="border rounded px-3 py-1 bg-gray-50">{{'Full Trade':'Tam İşlem','Close Only':'Yalnızca Kapat',Disabled:'Kapalı'}[clientTradingSettings.tradability]||clientTradingSettings.tradability}</div>
          </div>
        </div>
      </div>

      {/* Live Financial Information */}
      <div className="border-b">
        <div className="sirix-section-header flex justify-between items-center">
          <span className="text-red-500">Güncel Finansal Bilgiler</span>
          <div className="flex gap-2 flex-wrap">{financialActions || <>
            <button 
              className="sirix-btn text-xs" 
              style={{ backgroundColor: '#00BCD4', color: 'white' }}
              onClick={onOpenPosition}
              data-testid="new-position-btn"
            >
              <Plus size={12} /> New Position
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs" 
              onClick={() => onAddTransaction("deposit")}
              data-testid="deposit-btn"
            >
              <Plus size={12} /> Deposit
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs" 
              onClick={() => onAddTransaction("withdrawal")}
              data-testid="withdraw-btn"
            >
              <Minus size={12} /> Withdraw
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs"
              onClick={onCreditIn}
              data-testid="credit-in-btn"
            >
              <CreditCard size={12} /> Credit In
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs"
              onClick={onCreditOut}
              data-testid="credit-out-btn"
            >
              <CreditCard size={12} /> Credit Out
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary text-xs"
              onClick={onZeroBalance}
              disabled={processingZeroBalance}
              data-testid="zero-balance-btn"
            >
              {processingZeroBalance ? "Processing..." : "Zero Balance"}
            </button>
            </>}
          </div>
        </div>
        <div className="p-4 grid grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-gray-500">Bakiye:</label>
            <input data-testid="account-summary-balance" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.balance)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Kredi:</label>
            <input data-testid="account-summary-credit" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.credit)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Varlık:</label>
            <input data-testid="account-summary-equity" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.equity)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Açık Kâr / Zarar:</label>
            <input data-testid="account-summary-open-pl" type="text" className={`sirix-form-input font-mono ${financialData.openPL >= 0 ? 'text-green-600' : 'text-red-600'}`} value={formatCurrency(financialData.openPL)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Teminat:</label>
            <input data-testid="account-summary-margin" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.margin)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Serbest Teminat:</label>
            <input data-testid="account-summary-free-margin" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.freeMargin)} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Teminat Seviyesi:</label>
            <input data-testid="account-summary-margin-level" type="text" className={`sirix-form-input font-mono ${financialData.marginLevel < 100 ? 'text-red-600' : financialData.marginLevel < 200 ? 'text-orange-500' : 'text-green-600'}`} value={`${financialData.marginLevel.toFixed(2)}%`} readOnly />
          </div>
          <div>
            <label className="text-xs text-gray-500">Risk:</label>
            <input data-testid="account-summary-exposure" type="text" className="sirix-form-input font-mono" value={formatCurrency(financialData.exposure)} readOnly />
          </div>
        </div>
      </div>

      {/* Positions Tabs */}
      {tableContent || <>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex">
            {["OPEN", "PENDING", "CLOSED", "TRANSACTIONS"].map((tab) => (
              <button
                key={tab}
                className={`px-4 py-2 text-sm font-medium border ${
                  positionsTab === tab 
                    ? "bg-[#00BCD4] text-white border-[#00BCD4]" 
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                } ${tab === "OPEN" ? "rounded-l" : ""} ${tab === "TRANSACTIONS" ? "rounded-r" : ""}`}
                onClick={() => setPositionsTab(tab)}
              >
                {tab === "TRANSACTIONS" ? "💲 " : "📋 "}{tab}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            {positionsTab === "OPEN" && (
              <>
                <button className="sirix-btn sirix-btn-secondary text-xs">📋 Pending</button>
                <button 
                  className="sirix-btn sirix-btn-secondary text-xs"
                  onClick={() => onOpenPosition && onOpenPosition()}
                >
                  📋 Open
                </button>
                <button 
                  className="sirix-btn sirix-btn-secondary text-xs"
                  onClick={onEditClick}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="sirix-btn sirix-btn-secondary text-xs"
                  onClick={onCloseClick}
                >
                  📋 Close
                </button>
                <button 
                  className="sirix-btn sirix-btn-secondary text-xs"
                  onClick={onCloseAll}
                >
                  Close All
                </button>
              </>
            )}
            {positionsTab === "PENDING" && (
              <>
                <button className="sirix-btn sirix-btn-secondary text-xs">📋 Pending</button>
                <button 
                  className="sirix-btn sirix-btn-secondary text-xs"
                  onClick={() => onOpenPosition && onOpenPosition()}
                >
                  📋 Open
                </button>
                <button className="sirix-btn sirix-btn-secondary text-xs">✏️ Edit</button>
                <button className="sirix-btn sirix-btn-secondary text-xs">🗑️ Delete</button>
                <button className="sirix-btn sirix-btn-secondary text-xs">Delete All</button>
              </>
            )}
          </div>
        </div>

        {/* OPEN Positions Table */}
        {positionsTab === "OPEN" && (
          <div className="overflow-auto">
            <table className="sirix-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input 
                      type="checkbox" 
                      checked={selectedPositions.length === positions.length && positions.length > 0}
                      onChange={onSelectAll}
                    />
                  </th>
                  <th>Open Position ID</th>
                  <th>Instrument</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Units</th>
                  <th>Open Time</th>
                  <th>Open Rate</th>
                  <th>Current Rate</th>
                  <th>Trader Spread</th>
                  <th>Opened From</th>
                  <th>Swap</th>
                  <th>Commission</th>
                  <th>P&L</th>
                  <th className="text-center">
                    <div className="flex items-center justify-center gap-1 text-orange-600">
                      <Activity size={12} />
                      Virtual Price
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos) => {
                  const pl = calculatePositionPL(pos);
                  return (
                    <tr 
                      key={pos.id} 
                      className={`cursor-pointer hover:bg-[#e0f7fa] ${selectedPositions.includes(pos.id) ? 'bg-[#b2ebf2]' : ''}`}
                      onDoubleClick={() => onEditPosition(pos)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={selectedPositions.includes(pos.id)}
                          onChange={() => onSelectPosition(pos.id)}
                        />
                      </td>
                      <td className="font-semibold">📋 {pos.id}</td>
                      <td>
                        <span className="text-yellow-500">🥇</span> {pos.symbol}
                      </td>
                      <td className={pos.side === "buy" ? "text-green-600" : "text-red-600"}>
                        📄 {pos.side === "buy" ? "Buy" : "Sell"}
                      </td>
                      <td className="num">{pos.quantity}</td>
                      <td>{pos.units || "Oz."}</td>
                      <td className="font-mono text-xs">{pos.opened_at}</td>
                      <td className="num">{pos.entry_price?.toLocaleString()}</td>
                      <td className="num">{pos.current_price?.toLocaleString()}</td>
                      <td className="num" onClick={(e) => e.stopPropagation()}>
                        {editingSpread === pos.id ? (
                          <input
                            type="number"
                            value={spreadValue}
                            onChange={(e) => setSpreadValue(e.target.value)}
                            onBlur={() => handleSpreadSave(pos.id)}
                            onKeyDown={(e) => handleSpreadKeyDown(e, pos.id)}
                            className="w-16 px-1 py-0.5 border rounded text-center text-sm"
                            autoFocus
                            data-testid={`spread-input-${pos.id}`}
                          />
                        ) : (
                          <span 
                            className="cursor-pointer hover:bg-yellow-100 px-2 py-0.5 rounded"
                            onClick={() => handleSpreadEdit(pos)}
                            title="Click to edit spread"
                            data-testid={`spread-value-${pos.id}`}
                          >
                            {pos.spread || 0}
                          </span>
                        )}
                      </td>
                      <td><span className="text-xs">{pos.source || "Broker"}</span></td>
                      <td className="num">${pos.swap?.toFixed(2) || "0.00"}</td>
                      <td className="num">${pos.commission?.toFixed(2) || "0.00"}</td>
                      <td className={`num font-semibold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {pl >= 0 ? '' : '-'}${Math.abs(pl).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </td>
                      <td>
                        <button
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                            pos.virtualPriceActive 
                              ? 'bg-orange-100 text-orange-700 border border-orange-300 animate-pulse' 
                              : 'bg-gray-100 hover:bg-orange-50 text-gray-600 hover:text-orange-600 border border-transparent hover:border-orange-200'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Virtual Price Set button clicked for position:', pos.id);
                            onVirtualPrice(pos);
                          }}
                          data-testid={`virtual-price-btn-${pos.id}`}
                          title="Set virtual price for this position (Admin Only)"
                        >
                          <Activity size={12} />
                          {pos.virtualPriceActive ? 'Active' : 'Set'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PENDING Orders Table */}
        {positionsTab === "PENDING" && (
          <div className="overflow-auto">
            <table className="sirix-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}><input type="checkbox" /></th>
                  <th>Order ID</th>
                  <th>Instrument</th>
                  <th>Type</th>
                  <th>Side</th>
                  <th>Volume</th>
                  <th>Price</th>
                  <th>Stop Loss</th>
                  <th>Take Profit</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingOrders.map((order) => {
                  const orderType = order.type?.toLowerCase() || '';
                  const orderSide = order.side?.toLowerCase() || '';
                  const isBuy = orderSide === 'buy' || orderSide.includes('buy');
                  return (
                    <tr key={order.id} className="cursor-pointer hover:bg-[#e0f7fa]">
                      <td><input type="checkbox" /></td>
                      <td className="font-mono text-xs">{order.id?.slice(0, 8)}...</td>
                      <td><span className="text-blue-500">📈</span> {order.symbol}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${orderType === 'limit' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                          {orderType?.toUpperCase()}
                        </span>
                      </td>
                      <td className={isBuy ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                        {orderSide?.toUpperCase()}
                      </td>
                      <td className="num">{order.volume || order.quantity || '-'}</td>
                      <td className="num font-mono">{order.price?.toFixed(5) || '-'}</td>
                      <td className="num font-mono text-red-500">{order.stop_loss?.toFixed(5) || '-'}</td>
                      <td className="num font-mono text-green-500">{order.take_profit?.toFixed(5) || '-'}</td>
                      <td className="font-mono text-xs">{order.created_at ? new Date(order.created_at).toLocaleString() : '-'}</td>
                      <td>
                        <button className="text-red-500 hover:text-red-700 text-xs">Cancel</button>
                      </td>
                    </tr>
                  );
                })}
                {pendingOrders.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-8 text-gray-400">No pending orders</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* CLOSED Positions Table */}
        {positionsTab === "CLOSED" && (
          <div className="overflow-auto">
            <table className="sirix-table">
              <thead>
                <tr>
                  <th>Position ID</th>
                  <th>Instrument</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Open Time</th>
                  <th>Close Time</th>
                  <th>Open Rate</th>
                  <th>Close Rate</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {closedPositions.length > 0 ? closedPositions.map((pos) => (
                  <tr key={pos.id} className="cursor-pointer hover:bg-[#e0f7fa]" onDoubleClick={() => onEditClosedPosition(pos)}>
                    <td className="font-semibold">{pos.id}</td>
                    <td>{pos.symbol}</td>
                    <td className={pos.side === "buy" ? "text-green-600" : "text-red-600"}>{pos.side === "buy" ? "Buy" : "Sell"}</td>
                    <td className="num">{pos.quantity}</td>
                    <td className="font-mono text-xs">{pos.opened_at}</td>
                    <td className="font-mono text-xs">{new Date(pos.closed_at).toLocaleString()}</td>
                    <td className="num">{pos.entry_price}</td>
                    <td className="num">{pos.close_price}</td>
                    <td className={`num ${pos.profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>${pos.profit_loss?.toFixed(2)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="text-center py-8 text-gray-400">No closed positions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* TRANSACTIONS Table */}
        {positionsTab === "TRANSACTIONS" && (
          <div className="overflow-auto">
            <table className="sirix-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action Type</th>
                  <th>Action ID</th>
                  <th>Source</th>
                  <th>Source ID</th>
                  <th>Funds In</th>
                  <th>Funds Out</th>
                  <th>Comment</th>
                  <th>Sub Type</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={tx.id || idx}>
                    <td className="font-mono text-xs">{tx.time}</td>
                    <td>{tx.type}</td>
                    <td>{tx.id}</td>
                    <td>{tx.source}</td>
                    <td>{tx.source_id}</td>
                    <td className="num text-green-600">{tx.funds_in ? `$${tx.funds_in.toLocaleString()}` : "-"}</td>
                    <td className="num text-red-600">{tx.funds_out ? `$${tx.funds_out.toLocaleString()}` : "-"}</td>
                    <td>{tx.comment || "-"}</td>
                    <td>{tx.sub_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="sirix-table-footer mt-2">
          <div>Count={positionsTab === "OPEN" ? positions.length : positionsTab === "PENDING" ? pendingOrders.length : positionsTab === "CLOSED" ? closedPositions.length : transactions.length}</div>
          {positionsTab === "OPEN" && (
            <div className="flex gap-4">
              <span>Swap: ${positions.reduce((s, p) => s + (p.swap || 0), 0).toFixed(2)}</span>
              <span>Comm: ${positions.reduce((s, p) => s + (p.commission || 0), 0).toFixed(2)}</span>
              <span className={financialData.openPL >= 0 ? 'text-green-600' : 'text-red-600'}>
                P&L: {financialData.openPL >= 0 ? '+' : ''}{financialData.openPL.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </span>
            </div>
          )}
        </div>
      </div>
      </>}
    </div>
  );
};

// PERSONAL DETAILS TAB
const PersonalDetailsTab = ({ client, onUpdate, scoped = false, canEdit = true }) => {
  const [formData, setFormData] = useState({
    full_name: client.full_name || "",
    broker: client.broker || "SirixBroker",
    personal_id: client.personal_id || "",
    phone: client.phone || "",
    email: client.email || "",
    source: client.source || "",
    comment: client.comments || "",
    country: client.country || "",
    state: client.region || "",
    city: client.city || "",
    address: client.address || "",
    zip_code: client.postal_code || ""
  });

  return (
    <fieldset disabled={!canEdit} className="p-4 grid grid-cols-2 gap-6">
      <div>
        <div className="sirix-section-header mb-4">Genel</div>
        <div className="space-y-3">
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Ad Soyad:</label><input data-testid="account-personal-name" type="text" className="sirix-form-input flex-1" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Kurum:</label><input data-testid="account-personal-broker" type="text" className="sirix-form-input flex-1 bg-gray-100" value={formData.broker} readOnly /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Kimlik No:</label><input data-testid="account-personal-id" type="text" className="sirix-form-input flex-1" value={formData.personal_id} onChange={(e) => setFormData({...formData, personal_id: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Telefon:</label><input data-testid="account-personal-phone" type="text" className="sirix-form-input flex-1" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">E-posta:</label><input data-testid="account-personal-email" type="email" className="sirix-form-input flex-1" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Oluşturulma:</label><input data-testid="account-personal-created" type="text" className="sirix-form-input flex-1 bg-gray-100" value={new Date(client.created_at).toLocaleString('tr-TR')} readOnly /></div>
          <div className="flex items-start gap-4"><label className="w-28 text-sm text-gray-600 pt-2">Açıklama:</label><textarea data-testid="account-personal-comment" className="sirix-form-input flex-1 h-24" value={formData.comment} onChange={(e) => setFormData({...formData, comment: e.target.value})} /></div>
        </div>
      </div>
      <div>
        <div className="sirix-section-header mb-4">Yatırımcı Oturumu</div>
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Son Görülme:</label><input data-testid="account-last-seen" type="text" className="sirix-form-input flex-1 bg-gray-100" value={client.last_activity ? new Date(client.last_activity).toLocaleString('tr-TR') : "—"} readOnly /></div>
        </div>
        <div className="sirix-section-header mb-4">Adres</div>
        <div className="space-y-3">
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Ülke:</label><input data-testid="account-personal-country" type="text" className="sirix-form-input flex-1" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Bölge:</label><input data-testid="account-personal-region" type="text" className="sirix-form-input flex-1" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Şehir:</label><input data-testid="account-personal-city" type="text" className="sirix-form-input flex-1" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Adres:</label><input data-testid="account-personal-address" type="text" className="sirix-form-input flex-1" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></div>
          <div className="flex items-center gap-4"><label className="w-28 text-sm text-gray-600">Posta Kodu:</label><input data-testid="account-personal-postal-code" type="text" className="sirix-form-input flex-1" value={formData.zip_code} onChange={(e) => setFormData({...formData, zip_code: e.target.value})} /></div>
        </div>
      </div>
      {scoped && canEdit && <button className="sirix-btn sirix-btn-primary" data-testid="account-personal-save" onClick={() => onUpdate({full_name:formData.full_name,personal_id:formData.personal_id,email:formData.email,phone:formData.phone,comments:formData.comment,country:formData.country,region:formData.state,city:formData.city,address:formData.address,postal_code:formData.zip_code,source:formData.source})}>Değişiklikleri Kaydet</button>}
    </fieldset>
  );
};

// SECURITY TAB
const SecurityTab = ({ client, onUpdate }) => {
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [security, setSecurity] = useState(client.security || {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPermissionGroupModal, setShowPermissionGroupModal] = useState(false);
  const [showEditGroupsModal, setShowEditGroupsModal] = useState(false);
  const [selectedGroupMode, setSelectedGroupMode] = useState('all'); // 'all' or 'selected'
  
  // All permission categories
  const permissionCategories = {
    accountTrading: {
      label: "Account Trading",
      permissions: [
        { key: 'account_trading_read', label: 'Read' },
        { key: 'account_trading_edit', label: 'Edit' },
        { key: 'account_trading_daily_pnl', label: "Trader's Daily P&L" },
        { key: 'account_trading_edit_comments', label: 'Edit Comments on Money Transfers' },
        { key: 'account_trading_deposit_withdraw', label: 'Deposit / Withdraw' },
        { key: 'account_trading_deposit_withdraw_sub', label: 'Deposit / Withdraw (Sub)' },
        { key: 'account_trading_credit', label: 'Credit' },
        { key: 'account_trading_credit_edit', label: 'Credit Edit' },
        { key: 'account_trading_broker_exposure', label: 'Broker Exposure' },
      ]
    },
    accountPersonal: {
      label: "Account Personal Details",
      permissions: [
        { key: 'account_personal_read', label: 'Read' },
        { key: 'account_personal_create_edit', label: 'Create / Edit / Restore' },
        { key: 'account_personal_edit_broker_permissions', label: 'Edit Broker permissions' },
      ]
    },
    tradingTerms: {
      label: "Trading Terms",
      permissions: [
        { key: 'trading_terms_read', label: 'Read' },
        { key: 'trading_terms_edit_delete', label: 'Edit / Delete' },
      ]
    },
    labels: {
      label: "Labels",
      permissions: [
        { key: 'labels_read', label: 'Read' },
        { key: 'labels_edit', label: 'Edit' },
      ]
    },
    journal: {
      label: "Journal",
      permissions: [
        { key: 'journal_read', label: 'Read' },
      ]
    },
    newsMode: {
      label: "News Mode",
      permissions: [
        { key: 'news_mode_edit', label: 'Edit' },
      ]
    },
    apiToken: {
      label: "Api Token",
      permissions: [
        { key: 'api_token_edit', label: 'Edit' },
      ]
    },
    organizationSymbols: {
      label: "Organization Symbols",
      permissions: [
        { key: 'organization_symbols_read', label: 'Read' },
        { key: 'organization_symbols_edit', label: 'Edit' },
      ]
    },
    editPosition: {
      label: "Edit Position",
      permissions: [
        { key: 'position_open_specific_price', label: 'Open position with specific price' },
        { key: 'position_close_specific_price', label: 'Close position with specific price' },
        { key: 'position_specific_symbol', label: 'Position with specific symbol' },
        { key: 'position_specific_time', label: 'Position with specific time' },
        { key: 'position_specific_size', label: 'Position with specific size' },
        { key: 'position_specific_swap', label: 'Position with specific swap' },
        { key: 'position_specific_action_type', label: 'Position with specific action type' },
        { key: 'position_remove', label: 'Position remove' },
        { key: 'position_reopen', label: 'Position reopen' },
      ]
    },
  };

  // Fetch permission groups
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');  // Manager uses 'token'
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        const groupsRes = await managerFetch(`${API_URL}/api/permission-groups`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const groups = await groupsRes.json();
        if (Array.isArray(groups)) {
          setPermissionGroups(groups);
        }
        
        // Set selected group mode based on security settings
        if (security.selected_permission_groups?.length > 0) {
          setSelectedGroupMode('selected');
        }
      } catch (error) {
        console.error('Error fetching permission groups:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []); // Empty dependency - fetch once on mount

  const handlePermissionChange = (key, value) => {
    setSecurity(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Get account ID from client's trade_accounts
      const accountId = client.trade_accounts?.[0]?.id || client.id;
      
      await managerFetch(`${API_URL}/api/accounts/${accountId}/security`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(security)
      });
      
      toast.success('Security settings saved');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPermissionGroup = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const accountId = client.trade_accounts?.[0]?.id || client.id;
      
      await managerFetch(`${API_URL}/api/accounts/${accountId}/permission-group`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permission_group_id: groupId })
      });
      
      setSecurity(prev => ({ ...prev, permission_group_id: groupId }));
      toast.success('Permission group assigned');
      setShowPermissionGroupModal(false);
    } catch (error) {
      toast.error('Failed to assign permission group');
    }
  };

  const handleSelectAllPermissions = () => {
    const allPermissions = {};
    Object.values(permissionCategories).forEach(category => {
      category.permissions.forEach(perm => {
        allPermissions[perm.key] = true;
      });
    });
    setSecurity(prev => ({ ...prev, permissions: allPermissions }));
  };

  const handleMonitoringUser = () => {
    // Only read permissions
    const readPermissions = {};
    Object.values(permissionCategories).forEach(category => {
      category.permissions.forEach(perm => {
        readPermissions[perm.key] = perm.key.includes('_read');
      });
    });
    setSecurity(prev => ({ ...prev, permissions: readPermissions }));
  };

  const currentGroup = permissionGroups.find(g => g.id === security.permission_group_id);

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00BCD4] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* General Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="sirix-section-header" style={{ padding: 0 }}>General</div>
          <div className="flex gap-2">
            <button 
              className="sirix-btn sirix-btn-secondary"
              onClick={() => toast.info('Password change initiated')}
            >
              Change Password
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary"
              onClick={() => setShowPermissionGroupModal(true)}
            >
              Set Account Permission Group
            </button>
            <button 
              className="sirix-btn sirix-btn-secondary"
              onClick={() => setShowEditGroupsModal(true)}
            >
              Edit Brokerage Permission Groups
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-600 w-40">Permission Group:</label>
            <select 
              className="sirix-form-select flex-1"
              value={security.permission_group_id || ''}
              onChange={(e) => setSecurity(prev => ({ ...prev, permission_group_id: e.target.value }))}
            >
              <option value="">-- Select Group --</option>
              {permissionGroups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm text-gray-600 w-40">Administrator Permissions:</label>
            <select 
              className="sirix-form-select flex-1"
              value={security.administrator_permissions ? 'yes' : 'no'}
              onChange={(e) => setSecurity(prev => ({ ...prev, administrator_permissions: e.target.value === 'yes' }))}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        </div>
      </div>

      {/* SIRIX Broker Permissions Section */}
      <div className="border rounded">
        <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-semibold text-[#00BCD4]">SIRIX Broker Permissions</span>
            <label className="flex items-center gap-2 text-sm">
              <input 
                type="checkbox"
                checked={security.broker_permissions_enabled !== false}
                onChange={(e) => setSecurity(prev => ({ ...prev, broker_permissions_enabled: e.target.checked }))}
              />
              <span className={security.broker_permissions_enabled !== false ? 'text-green-600' : 'text-red-600'}>
                {security.broker_permissions_enabled !== false ? 'Yes' : 'No'}
              </span>
            </label>
          </div>
          <div className="flex gap-2">
            <button 
              className="text-xs px-3 py-1 bg-[#00BCD4] text-white rounded hover:bg-[#00ACC1]"
              onClick={handleSelectAllPermissions}
            >
              Select All Permissions
            </button>
            <button 
              className="text-xs px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
              onClick={handleMonitoringUser}
            >
              Monitoring User
            </button>
          </div>
        </div>
        
        <div className="p-4">
          {/* Permission Groups Selection */}
          <div className="mb-4 pb-4 border-b">
            <div className="font-medium mb-2">Permission Groups:</div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input 
                  type="radio" 
                  name="permGroupMode" 
                  checked={selectedGroupMode === 'all'}
                  onChange={() => setSelectedGroupMode('all')}
                />
                All
              </label>
              <label className="flex items-center gap-2">
                <input 
                  type="radio" 
                  name="permGroupMode" 
                  checked={selectedGroupMode === 'selected'}
                  onChange={() => setSelectedGroupMode('selected')}
                />
                Selected
              </label>
              {selectedGroupMode === 'selected' && (
                <select 
                  className="sirix-form-select"
                  multiple
                  value={security.selected_permission_groups || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, opt => opt.value);
                    setSecurity(prev => ({ ...prev, selected_permission_groups: selected }));
                  }}
                  style={{ height: '80px', minWidth: '200px' }}
                >
                  {permissionGroups.map(group => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {/* Permission Checkboxes Grid */}
          <div className="grid grid-cols-4 gap-6 text-sm">
            {Object.entries(permissionCategories).map(([categoryKey, category]) => (
              <div key={categoryKey}>
                <div className="font-medium mb-2 text-gray-700">{category.label}:</div>
                {category.permissions.map((perm) => (
                  <label key={perm.key} className="flex items-center gap-2 mb-1">
                    <input 
                      type="checkbox"
                      checked={security.permissions?.[perm.key] || false}
                      onChange={(e) => handlePermissionChange(perm.key, e.target.checked)}
                    />
                    <span className="text-gray-600">{perm.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API Access Section */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-600 w-40">Certificate (for API access):</label>
          <input 
            type="text"
            className="sirix-form-input flex-1"
            value={security.api_certificate || ''}
            onChange={(e) => setSecurity(prev => ({ ...prev, api_certificate: e.target.value }))}
            placeholder="Certificate"
          />
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm text-gray-600 w-40">Token (For API access):</label>
          <input 
            type="text"
            className="sirix-form-input flex-1"
            value={security.api_token || ''}
            readOnly
            placeholder="Token"
          />
          <button 
            className="sirix-btn sirix-btn-secondary text-xs"
            onClick={() => toast.info('Generating new API token...')}
          >
            Generate
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button 
          className="sirix-btn sirix-btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Security Settings'}
        </button>
      </div>

      {/* Set Account Permission Group Modal */}
      {showPermissionGroupModal && (
        <div className="sirix-modal-overlay" onClick={() => setShowPermissionGroupModal(false)}>
          <div className="sirix-modal" style={{ width: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg text-[#00BCD4]">Set Account Permission Group</h2>
              <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={() => setShowPermissionGroupModal(false)}>×</button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">Select a permission group to assign to this account:</p>
              <select 
                className="sirix-form-select w-full mb-4"
                value={security.permission_group_id || ''}
                onChange={(e) => handleSetPermissionGroup(e.target.value)}
              >
                <option value="">-- Select Group --</option>
                {permissionGroups.map(group => (
                  <option key={group.id} value={group.id}>
                    {group.name} {group.is_ret_group ? '(RET)' : ''}
                  </option>
                ))}
              </select>
              {currentGroup && (
                <div className="text-xs text-gray-500">
                  Current: {currentGroup.name} | Currency: {currentGroup.default_currency}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button className="sirix-btn sirix-btn-secondary" onClick={() => setShowPermissionGroupModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Brokerage Permission Groups Modal */}
      {showEditGroupsModal && (
        <EditBrokeragePermissionGroupsModal 
          groups={permissionGroups}
          onClose={() => setShowEditGroupsModal(false)}
          onUpdate={(updatedGroups) => setPermissionGroups(updatedGroups)}
        />
      )}
    </div>
  );
};

// Edit Brokerage Permission Groups Modal
const EditBrokeragePermissionGroupsModal = ({ groups, onClose, onUpdate }) => {
  const [localGroups, setLocalGroups] = useState(groups || []);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupCurrency, setNewGroupCurrency] = useState('TRY');
  const [newGroupTradingTerms, setNewGroupTradingTerms] = useState('ZG Default TRY');
  const [saving, setSaving] = useState(false);

  // Sync localGroups when groups prop changes
  useEffect(() => {
    if (groups && groups.length > 0) {
      setLocalGroups(groups);
    }
  }, [groups]);

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Group name is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const res = await managerFetch(`${API_URL}/api/permission-groups`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newGroupName,
          default_currency: newGroupCurrency,
          default_trading_terms: newGroupTradingTerms,
          is_ret_group: newGroupName.toLowerCase().includes('ret')
        })
      });
      
      const data = await res.json();
      if (res.ok && data.success) {
        setLocalGroups(prev => [...prev, data.group]);
        setNewGroupName('');
        setNewGroupTradingTerms('ZG Default TRY');
        toast.success('Permission group added');
        onUpdate([...localGroups, data.group]);
      } else {
        // Show specific error message from backend
        toast.error(data.detail || 'Failed to add permission group');
      }
    } catch (error) {
      toast.error('Failed to add permission group');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this permission group?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      await managerFetch(`${API_URL}/api/permission-groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updated = localGroups.filter(g => g.id !== groupId);
      setLocalGroups(updated);
      onUpdate(updated);
      toast.success('Permission group deleted');
    } catch (error) {
      toast.error('Failed to delete permission group');
    }
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg text-[#00BCD4]">Edit Brokerage Permission Groups</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl" onClick={onClose}>×</button>
        </div>
        
        <div className="p-6">
          {/* Table */}
          <div className="border rounded mb-4 max-h-80 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 border-b">Name</th>
                  <th className="text-left px-4 py-2 border-b">Default trading terms for LXAPI</th>
                  <th className="text-left px-4 py-2 border-b">Default currency for LXAPI account</th>
                  <th className="text-center px-4 py-2 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {localGroups.map((group) => (
                  <tr key={group.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 border-b">
                      {group.name}
                      {group.is_ret_group && <span className="ml-2 text-xs bg-blue-100 text-blue-600 px-1 rounded">RET</span>}
                    </td>
                    <td className="px-4 py-2 border-b">{group.default_trading_terms || '-'}</td>
                    <td className="px-4 py-2 border-b">{group.default_currency}</td>
                    <td className="px-4 py-2 border-b text-center">
                      <button 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Add New Group */}
          <div className="bg-gray-50 p-4 rounded border">
            <div className="font-medium mb-3">Add New Permission Group</div>
            <div className="grid grid-cols-3 gap-3">
              <input 
                type="text"
                className="sirix-form-input"
                placeholder="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
              <select 
                className="sirix-form-select"
                value={newGroupTradingTerms}
                onChange={(e) => setNewGroupTradingTerms(e.target.value)}
              >
                {TRADING_TERMS.map(term => (
                  <option key={term.id} value={term.name}>{term.name}</option>
                ))}
              </select>
              <select 
                className="sirix-form-select"
                value={newGroupCurrency}
                onChange={(e) => setNewGroupCurrency(e.target.value)}
              >
                <option value="TRY">TRY</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <button 
              className="sirix-btn sirix-btn-primary mt-3"
              onClick={handleAddGroup}
              disabled={saving}
            >
              {saving ? 'Adding...' : '+ Add Group'}
            </button>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
          <button className="sirix-btn sirix-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

// EDIT POSITION MODAL - Full featured
const EditPositionModal = ({ position, client, financialData, allPositions, onClose, onSave }) => {
  // Parse date from opened_at string (format: "3/18/2026 7:12:00 PM" or similar)
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: new Date().toISOString().split('T')[0], time: '12:00' };
    const parts = dateTimeStr.split(' ');
    const datePart = parts[0];
    const timePart = parts[1] || '12:00:00';
    // Convert date from M/D/YYYY to YYYY-MM-DD
    const dateComponents = datePart.split('/');
    if (dateComponents.length === 3) {
      const [month, day, year] = dateComponents;
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      // Convert time to HH:MM format
      const timeComponents = timePart.split(':');
      const formattedTime = `${timeComponents[0].padStart(2, '0')}:${timeComponents[1] || '00'}`;
      return { date: formattedDate, time: formattedTime };
    }
    return { date: new Date().toISOString().split('T')[0], time: '12:00' };
  };

  const parsedDateTime = parseDateTime(position.opened_at);
  
  const [formData, setFormData] = useState({
    ...position,
    open_date: parsedDateTime.date,
    open_time: parsedDateTime.time
  });

  const calculateProfit = (data) => {
    if (data.side === "buy") {
      return (data.current_price - data.entry_price) * data.quantity;
    } else {
      return (data.entry_price - data.current_price) * data.quantity;
    }
  };

  const currentPL = calculateProfit(formData);
  const originalPL = calculateProfit(position);
  const plChange = currentPL - originalPL;

  // Calculate new financials based on edit
  const newOpenPL = financialData.openPL - originalPL + currentPL;
  const newEquity = financialData.balance + newOpenPL;
  const newMargin = allPositions.reduce((sum, p) => {
    if (p.id === formData.id) {
      return sum + (Math.abs(formData.quantity * formData.current_price) / (formData.leverage || 100));
    }
    return sum + (Math.abs(p.quantity * p.current_price) / (p.leverage || 100));
  }, 0);
  const newFreeMargin = newEquity - newMargin;
  const newMarginLevel = newMargin > 0 ? (newEquity / newMargin) * 100 : 0;

  const handleSave = () => {
    onSave({
      ...formData,
      opened_at: `${formData.open_date} ${formData.open_time}`
    });
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 800 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b">
          <h2 className="text-xl text-[#00BCD4]">Edit open position: {position.id}</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>×</button>
        </div>
        
        {/* Gray Info Bar - 2x2 Grid */}
        <div className="bg-gray-100 px-8 py-5">
          <div className="grid grid-cols-2 gap-y-3 gap-x-8">
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Account ID:</span>
              <span className="font-mono text-gray-900">{client.id?.slice(0, 8)}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Equity:</span>
              <span className="font-mono text-gray-900">${newEquity.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Margin Level:</span>
              <span className={`font-mono ${newMarginLevel < 100 ? 'text-red-500' : 'text-green-500'}`}>{newMarginLevel.toFixed(2)}%</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Free Margin:</span>
              <span className="font-mono text-gray-900">${newFreeMargin.toLocaleString(undefined, {minimumFractionDigits: 3})}</span>
            </div>
          </div>
        </div>

        {/* Position Section */}
        <div className="px-8 py-6">
          <div className="text-[#00BCD4] font-medium text-lg mb-8">Position</div>
          
          {/* Form Grid */}
          <div className="space-y-7">
            {/* Row 1: Position ID | Type */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Position ID:</label>
                <input type="text" className="sirix-form-input flex-1 py-2.5" value={formData.id} readOnly />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Type:</label>
                <select className="sirix-form-select flex-1 py-2.5" value={formData.side} onChange={(e) => setFormData({...formData, side: e.target.value})}>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
            </div>

            {/* Row 2: Instrument | Amount */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Instrument:</label>
                <select className="sirix-form-select flex-1 py-2.5" value={formData.symbol} onChange={(e) => setFormData({...formData, symbol: e.target.value})}>
                  <option value="XAUUSD">XAUUSD</option>
                  <option value="EURUSD">EURUSD</option>
                  <option value="BTCUSD">BTCUSD</option>
                  <option value="A1CAP-IST">A1CAP-IST</option>
                  <option value="VERUS-IST">VERUS-IST</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Amount:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})} />
                <span className="text-sm text-gray-500 ml-3">{formData.units || "Oz."}</span>
              </div>
            </div>

            {/* Row 3: Open price | Open time */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Open price:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.entry_price} onChange={(e) => setFormData({...formData, entry_price: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Open time:</label>
                <input 
                  type="date" 
                  className="sirix-form-input w-36 py-2.5" 
                  value={formData.open_date} 
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, open_date: e.target.value})} 
                />
                <input 
                  type="time" 
                  className="sirix-form-input w-24 py-2.5 ml-3" 
                  value={formData.open_time} 
                  onChange={(e) => setFormData({...formData, open_time: e.target.value})} 
                />
              </div>
            </div>

            {/* Row 4: Current rate | Current profit */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Current rate:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.current_price} onChange={(e) => setFormData({...formData, current_price: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Current profit:</label>
                <input type="text" className={`sirix-form-input flex-1 py-2.5 font-mono ${currentPL >= 0 ? 'text-green-500' : 'text-red-500'}`} value={`${currentPL >= 0 ? '' : '-'}$${Math.abs(currentPL).toLocaleString(undefined, {minimumFractionDigits: 2})}`} readOnly />
              </div>
            </div>

            {/* Row 5: Profit change | Swap */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Profit change:</label>
                <input type="text" className={`sirix-form-input flex-1 py-2.5 font-mono ${plChange >= 0 ? 'text-green-500' : 'text-red-500'}`} value={`${plChange >= 0 ? '+' : ''}$${plChange.toLocaleString(undefined, {minimumFractionDigits: 2})}`} readOnly />
              </div>
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Swap:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.swap || 0} onChange={(e) => setFormData({...formData, swap: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
            </div>

            {/* Row 6: Commission (only left) */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-32 text-sm text-gray-700 shrink-0">Commission:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.commission || 0} onChange={(e) => setFormData({...formData, commission: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
              <div></div>
            </div>

            {/* Row 7: Comment (full width) */}
            <div className="flex items-start mt-2">
              <label className="w-32 text-sm text-gray-700 shrink-0 pt-3">Comment:</label>
              <textarea className="sirix-form-input flex-1 h-28 resize-y py-2.5" value={formData.comment || ""} onChange={(e) => setFormData({...formData, comment: e.target.value})} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-4 px-8 py-5 border-t bg-gray-50">
          <button className="px-8 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded transition-colors font-medium">Delete</button>
          <button className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium" onClick={handleSave}>✓ Edit</button>
          <button className="px-8 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded transition-colors font-medium" onClick={onClose}>× Cancel</button>
        </div>
      </div>
    </div>
  );
};

// BULK EDIT MODAL - All fields like single edit
const BulkEditModal = ({ positions, client, financialData, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    side: positions[0]?.side || "buy",
    symbol: positions[0]?.symbol || "",
    entry_price: positions[0]?.entry_price || 0,
    current_price: positions[0]?.current_price || 0,
    swap: positions[0]?.swap || 0,
    commission: positions[0]?.commission || 0,
    open_date: positions[0]?.opened_at?.split(" ")[0] || "",
    open_time: positions[0]?.opened_at?.split(" ")[1] || ""
  });

  const calculateTotalPL = () => {
    return positions.reduce((sum, p) => {
      const pl = formData.side === "buy" 
        ? (formData.current_price - formData.entry_price) * p.quantity
        : (formData.entry_price - formData.current_price) * p.quantity;
      return sum + pl;
    }, 0);
  };

  const handleSave = () => {
    const updatedPositions = positions.map(p => ({
      ...p,
      side: formData.side,
      symbol: formData.symbol,
      entry_price: formData.entry_price,
      current_price: formData.current_price,
      swap: formData.swap,
      commission: formData.commission,
      opened_at: formData.open_date && formData.open_time ? `${formData.open_date} ${formData.open_time}` : p.opened_at
    }));
    onSave(updatedPositions);
  };

  const totalPL = calculateTotalPL();

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="sirix-modal-header">
          <h2 className="sirix-modal-title">Bulk Edit {positions.length} Positions ({positions[0]?.symbol})</h2>
          <button className="sirix-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="bg-gray-100 p-3 text-sm">
          <span className="text-gray-600">Editing positions:</span>
          {positions.map(p => (
            <span key={p.id} className="ml-2 bg-white px-2 py-0.5 rounded">{p.id}</span>
          ))}
        </div>

        <div className="sirix-modal-body p-4">
          <div className="sirix-section-header mb-4" style={{ padding: "8px 12px" }}>Position Settings (applies to all)</div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Type:</label>
              <select className="sirix-form-select flex-1" value={formData.side} onChange={(e) => setFormData({...formData, side: e.target.value})}>
                <option value="buy">Buy</option>
                <option value="sell">Sell</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Instrument:</label>
              <input type="text" className="sirix-form-input flex-1" value={formData.symbol} readOnly />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Open price:</label>
              <input type="number" className="sirix-form-input flex-1" value={formData.entry_price} onChange={(e) => setFormData({...formData, entry_price: parseFloat(e.target.value) || 0})} step="0.01" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Current price:</label>
              <input type="number" className="sirix-form-input flex-1" value={formData.current_price} onChange={(e) => setFormData({...formData, current_price: parseFloat(e.target.value) || 0})} step="0.01" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Open date:</label>
              <input type="text" className="sirix-form-input flex-1" value={formData.open_date} onChange={(e) => setFormData({...formData, open_date: e.target.value})} placeholder="Leave empty to keep original" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Open time:</label>
              <input type="text" className="sirix-form-input flex-1" value={formData.open_time} onChange={(e) => setFormData({...formData, open_time: e.target.value})} placeholder="Leave empty to keep original" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Swap:</label>
              <input type="number" className="sirix-form-input flex-1" value={formData.swap} onChange={(e) => setFormData({...formData, swap: parseFloat(e.target.value) || 0})} step="0.01" />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-32 text-sm">Commission:</label>
              <input type="number" className="sirix-form-input flex-1" value={formData.commission} onChange={(e) => setFormData({...formData, commission: parseFloat(e.target.value) || 0})} step="0.01" />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-4 border-t">
              <label className="w-32 text-sm font-semibold">Combined P&L:</label>
              <span className={`font-mono text-lg ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString(undefined, {minimumFractionDigits: 2})}
              </span>
            </div>
          </div>
        </div>

        <div className="sirix-modal-footer">
          <button className="sirix-btn sirix-btn-primary" onClick={handleSave}>✓ Apply to All ({positions.length})</button>
          <button className="sirix-btn sirix-btn-secondary" onClick={onClose}>✗ Cancel</button>
        </div>
      </div>
    </div>
  );
};

// EDIT CLOSED POSITION MODAL - Full featured like SiRiX
const EditClosedPositionModal = ({ position, client, financialData, onClose, onSave, onReopen }) => {
  // Parse date from string (format: "3/18/2026 7:12:00 PM" or similar)
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: new Date().toISOString().split('T')[0], time: '12:00' };
    
    // Handle ISO date string
    if (dateTimeStr.includes('T')) {
      const dt = new Date(dateTimeStr);
      return { 
        date: dt.toISOString().split('T')[0], 
        time: `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}` 
      };
    }
    
    const parts = dateTimeStr.split(' ');
    const datePart = parts[0];
    const timePart = parts[1] || '12:00:00';
    // Convert date from M/D/YYYY to YYYY-MM-DD
    const dateComponents = datePart.split('/');
    if (dateComponents.length === 3) {
      const [month, day, year] = dateComponents;
      const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      // Convert time to HH:MM format
      const timeComponents = timePart.split(':');
      const formattedTime = `${timeComponents[0].padStart(2, '0')}:${timeComponents[1] || '00'}`;
      return { date: formattedDate, time: formattedTime };
    }
    return { date: new Date().toISOString().split('T')[0], time: '12:00' };
  };

  const parsedOpenDateTime = parseDateTime(position.opened_at);
  const parsedCloseDateTime = parseDateTime(position.closed_at);
  
  const [formData, setFormData] = useState({
    ...position,
    open_date: parsedOpenDateTime.date,
    open_time: parsedOpenDateTime.time,
    close_date: parsedCloseDateTime.date,
    close_time: parsedCloseDateTime.time,
    close_price: position.close_price || position.current_price || 0
  });
  
  // Get current date for max restriction
  const today = new Date().toISOString().split('T')[0];

  // Calculate P&L based on current form values
  const calculateProfit = (data) => {
    if (data.side === "buy") {
      return (data.close_price - data.entry_price) * data.quantity;
    } else {
      return (data.entry_price - data.close_price) * data.quantity;
    }
  };

  const currentPL = calculateProfit(formData);
  const originalPL = position.profit_loss || calculateProfit(position);
  const plChange = currentPL - originalPL;

  // Calculate updated financials
  const balanceChange = currentPL - originalPL;
  const newBalance = (client.balance || 100000000) + balanceChange;
  const newEquity = newBalance + (financialData?.credit || 0);
  const newMarginLevel = financialData?.margin > 0 ? (newEquity / financialData.margin) * 100 : 0;

  const handleSave = () => {
    onSave({
      ...formData,
      opened_at: `${formData.open_date} ${formData.open_time}`,
      closed_at: `${formData.close_date} ${formData.close_time}`,
      profit_loss: currentPL
    });
  };

  const handleReopen = () => {
    if (window.confirm(`Reopen position ${position.id}?\nThis will move it back to open positions.`)) {
      onReopen && onReopen(position);
    }
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 800 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b">
          <h2 className="text-xl text-[#00BCD4]">
            Edit closed position: {position.id}, {position.side === "buy" ? "Buy" : "Sell"} {position.quantity} {position.symbol}
          </h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>×</button>
        </div>
        
        {/* Gray Info Bar */}
        <div className="bg-gray-100 px-8 py-5">
          <div className="grid grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Account<br/>ID:</span>
              <input type="text" className="sirix-form-input flex-1 bg-white py-2.5" value={client.id?.slice(0, 8)} readOnly />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Equity:</span>
              <input type="text" className="sirix-form-input flex-1 bg-white font-mono py-2.5" value={`$${newEquity.toLocaleString(undefined, {minimumFractionDigits: 2})}`} readOnly />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-gray-600">Margin<br/>Level:</span>
              <input type="text" className={`sirix-form-input flex-1 bg-white font-mono py-2.5 ${newMarginLevel < 100 ? 'text-red-500' : 'text-green-500'}`} value={`${newMarginLevel.toFixed(2)}%`} readOnly />
            </div>
          </div>
        </div>

        {/* Position Section */}
        <div className="px-8 py-6">
          <div className="bg-gray-100 px-4 py-2.5 text-[#00BCD4] font-medium text-lg mb-8">Position</div>
          
          {/* Form Grid */}
          <div className="space-y-7">
            {/* Row 1: Closed Position ID | Type */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Closed Position ID:</label>
                <input type="text" className="sirix-form-input flex-1 py-2.5" value={formData.id} readOnly />
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Type:</label>
                <select className="sirix-form-select flex-1 py-2.5" value={formData.side} onChange={(e) => setFormData({...formData, side: e.target.value})}>
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
            </div>

            {/* Row 2: Instrument | Amount */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Instrument:</label>
                <select className="sirix-form-select flex-1 py-2.5" value={formData.symbol} onChange={(e) => setFormData({...formData, symbol: e.target.value})}>
                  <option value="XAUUSD">XAUUSD</option>
                  <option value="EURUSD">EURUSD</option>
                  <option value="BTCUSD">BTCUSD</option>
                  <option value="A1CAP-IST">A1CAP-IST</option>
                  <option value="VERUS-IST">VERUS-IST</option>
                </select>
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Amount:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})} />
                <span className="text-sm text-gray-500 ml-3">{formData.units || "Oz."}</span>
              </div>
            </div>

            {/* Row 3: Open price | Open time */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Open price:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.entry_price} onChange={(e) => setFormData({...formData, entry_price: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Open time:</label>
                <input 
                  type="date" 
                  className="sirix-form-input w-36 py-2.5" 
                  value={formData.open_date} 
                  max={today}
                  onChange={(e) => setFormData({...formData, open_date: e.target.value})} 
                />
                <input 
                  type="time" 
                  className="sirix-form-input w-24 py-2.5 ml-3" 
                  value={formData.open_time} 
                  onChange={(e) => setFormData({...formData, open_time: e.target.value})} 
                />
              </div>
            </div>

            {/* Row 4: Close price | Close time */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Close price:</label>
                <input type="number" className="sirix-form-input flex-1 py-2.5" value={formData.close_price} onChange={(e) => setFormData({...formData, close_price: parseFloat(e.target.value) || 0})} step="0.01" />
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Close time:</label>
                <input 
                  type="date" 
                  className="sirix-form-input w-36 py-2.5" 
                  value={formData.close_date} 
                  max={today}
                  onChange={(e) => setFormData({...formData, close_date: e.target.value})} 
                />
                <input 
                  type="time" 
                  className="sirix-form-input w-24 py-2.5 ml-3" 
                  value={formData.close_time} 
                  onChange={(e) => setFormData({...formData, close_time: e.target.value})} 
                />
              </div>
            </div>

            {/* Row 5: Current profit | Change in profit */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Current profit:</label>
                <input type="text" className={`sirix-form-input flex-1 py-2.5 font-mono ${currentPL >= 0 ? 'text-green-500' : 'text-red-500'}`} value={`${currentPL >= 0 ? '' : '-'}$${Math.abs(currentPL).toLocaleString(undefined, {minimumFractionDigits: 2})}`} readOnly />
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Change in profit:</label>
                <input type="text" className="sirix-form-input flex-1 py-2.5 font-mono" value={`$${plChange.toLocaleString(undefined, {minimumFractionDigits: 2})}`} readOnly />
              </div>
            </div>

            {/* Row 6: Swap | Commission */}
            <div className="grid grid-cols-2 gap-12">
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Swap:</label>
                <input type="text" className="sirix-form-input flex-1 py-2.5" value={`$${(formData.swap || 0).toFixed(2)}`} onChange={(e) => setFormData({...formData, swap: parseFloat(e.target.value.replace('$', '')) || 0})} />
              </div>
              <div className="flex items-center">
                <label className="w-36 text-sm text-gray-700 shrink-0">Commission:</label>
                <input type="text" className="sirix-form-input flex-1 py-2.5" value={`$${(formData.commission || 0).toFixed(2)}`} onChange={(e) => setFormData({...formData, commission: parseFloat(e.target.value.replace('$', '')) || 0})} />
              </div>
            </div>

            {/* Row 7: Comment (full width) */}
            <div className="flex items-start mt-2">
              <label className="w-36 text-sm text-gray-700 shrink-0 pt-3">Comment:</label>
              <textarea className="sirix-form-input flex-1 h-28 resize-y py-2.5" value={formData.comment || ""} onChange={(e) => setFormData({...formData, comment: e.target.value})} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-4 px-8 py-5 border-t bg-gray-50">
          <button className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium">Delete</button>
          <button className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium" onClick={handleReopen}>Reopen</button>
          <button className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium" onClick={handleSave}>✓ Edit</button>
          <button className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium" onClick={onClose}>× Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ADD TRANSACTION MODAL
const AddTransactionModal = ({ client, type, onClose, onSave }) => {
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState(new Date().toTimeString().slice(0, 5));
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    if (amount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    onSave(amount, type);
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 580, maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
        {/* Green/Orange Header */}
        <div className="px-6 py-4" style={{ background: type === 'deposit' ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-white flex items-center gap-2">
              {type === "deposit" ? <Plus size={20} /> : <Minus size={20} />}
              {type === "deposit" ? "Add Deposit" : "Add Withdrawal"}
            </h2>
            <button className="text-white hover:text-white/80 text-2xl" onClick={onClose}>×</button>
          </div>
        </div>
        
        {/* Account Info Bar */}
        <div className="bg-gray-100 px-6 py-3 border-b">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Account: <span className="font-mono font-semibold">{client?.id?.slice(0, 8) || client?.email}</span></span>
            <span className="text-gray-700">Current Balance: <span className="font-mono font-semibold text-green-600">${(client?.balance || 0).toLocaleString()}</span></span>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Amount Input */}
          <div className="flex items-center">
            <label className="w-32 text-black font-medium">Amount ($):</label>
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input 
                type="number" 
                className="sirix-form-input w-full pl-8 py-3 font-mono text-lg border border-gray-300 rounded" 
                value={amount} 
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                placeholder="0"
                data-testid="transaction-amount-input"
              />
            </div>
          </div>
          
          {/* Date/Time Row */}
          <div className="flex items-center">
            <label className="w-32 text-black font-medium">Date:</label>
            <div className="flex-1 flex items-center gap-4">
              <input 
                type="date" 
                className="sirix-form-input flex-1 py-3 border border-gray-300 rounded" 
                value={date} 
                onChange={(e) => setDate(e.target.value)} 
              />
              <label className="text-black font-medium">Time:</label>
              <input 
                type="time" 
                className="sirix-form-input flex-1 py-3 border border-gray-300 rounded" 
                value={time} 
                onChange={(e) => setTime(e.target.value)} 
              />
            </div>
          </div>
          
          {/* Method */}
          <div className="flex items-center">
            <label className="w-32 text-black font-medium">Method:</label>
            <select 
              className="sirix-form-select flex-1 py-3 border border-gray-300 rounded"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="bank_transfer">Bank Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="crypto">Cryptocurrency</option>
              <option value="wire">Wire Transfer</option>
              <option value="cash">Cash</option>
            </select>
          </div>
          
          {/* Reference */}
          <div className="flex items-center">
            <label className="w-32 text-black font-medium">Reference:</label>
            <input 
              type="text" 
              className="sirix-form-input flex-1 py-3 border border-gray-300 rounded" 
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Transaction reference (optional)"
            />
          </div>
          
          {/* Notes */}
          <div className="flex items-start">
            <label className="w-32 text-black font-medium pt-3">Notes:</label>
            <textarea 
              className="sirix-form-input flex-1 py-3 h-24 resize-none border border-gray-300 rounded" 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes (optional)"
            />
          </div>
        </div>
        
        {/* Footer Buttons */}
        <div className="flex justify-center gap-4 px-6 py-5 bg-gray-50 border-t">
          <button 
            className={`px-8 py-3 rounded font-medium text-white ${type === 'deposit' ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600'}`} 
            onClick={handleSave}
            data-testid="transaction-save-btn"
          >
            ✓ {type === "deposit" ? "Add Deposit" : "Add Withdrawal"}
          </button>
          <button 
            className="px-8 py-3 rounded font-medium bg-gray-200 hover:bg-gray-300 text-gray-700" 
            onClick={onClose}
          >
            × Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// CREDIT MODAL - For Credit In/Out operations
const CreditModal = ({ client, type, onClose, onSave }) => {
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState("");

  const handleSave = () => {
    if (amount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    onSave(amount, type);
  };

  const isCredit = type === "credit_in";
  const currentCredit = client?.credit || 0;

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 580, maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
        {/* Blue/Red Header */}
        <div className="px-6 py-4" style={{ background: isCredit ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl text-white flex items-center gap-2">
              <CreditCard size={20} />
              {isCredit ? "Credit In" : "Credit Out"}
            </h2>
            <button className="text-white hover:text-white/80 text-2xl" onClick={onClose}>×</button>
          </div>
        </div>
        
        {/* Account Info Bar */}
        <div className="bg-gray-100 px-6 py-3 border-b">
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Account: <span className="font-mono font-semibold">{client?.id?.slice(0, 8) || client?.email}</span></span>
            <span className="text-gray-700">Current Credit: <span className="font-mono font-semibold text-blue-600">${currentCredit.toLocaleString()}</span></span>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Info Message */}
          <div className={`rounded-lg p-4 ${isCredit ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            <Info size={16} className="inline mr-2" />
            {isCredit 
              ? "Credit In will increase the account's credit line and equity."
              : "Credit Out will decrease the account's credit line and equity."}
          </div>
          
          {/* Amount Input */}
          <div className="flex items-center">
            <label className="w-32 text-black font-medium">Amount ($):</label>
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input 
                type="number" 
                className="sirix-form-input w-full pl-8 py-3 font-mono text-lg border border-gray-300 rounded" 
                value={amount} 
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                placeholder="0"
                data-testid="credit-amount-input"
              />
            </div>
          </div>
          
          {/* Notes */}
          <div className="flex items-start">
            <label className="w-32 text-black font-medium pt-3">Notes:</label>
            <textarea 
              className="sirix-form-input flex-1 py-3 h-24 resize-none border border-gray-300 rounded" 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for credit adjustment (optional)"
            />
          </div>
          
          {/* Summary - New Credit Preview */}
          {amount > 0 && (
            <div className={`rounded-lg p-4 ${isCredit ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex justify-between items-center">
                <span className="text-black font-medium">New Credit after {isCredit ? 'Credit In' : 'Credit Out'}:</span>
                <span className={`font-mono font-bold text-xl ${isCredit ? 'text-blue-600' : 'text-red-600'}`}>
                  ${isCredit 
                    ? (currentCredit + amount).toLocaleString() 
                    : Math.max(0, currentCredit - amount).toLocaleString()}
                </span>
              </div>
              {!isCredit && amount > currentCredit && (
                <p className="text-red-500 text-sm mt-2">⚠️ Warning: Amount exceeds current credit balance</p>
              )}
            </div>
          )}
        </div>
        
        {/* Footer Buttons */}
        <div className="flex justify-center gap-4 px-6 py-5 bg-gray-50 border-t">
          <button 
            className={`px-8 py-3 rounded font-medium text-white ${isCredit ? 'bg-blue-500 hover:bg-blue-600' : 'bg-red-500 hover:bg-red-600'}`} 
            onClick={handleSave}
            data-testid="credit-save-btn"
          >
            ✓ {isCredit ? "Add Credit" : "Remove Credit"}
          </button>
          <button 
            className="px-8 py-3 rounded font-medium bg-gray-200 hover:bg-gray-300 text-gray-700" 
            onClick={onClose}
          >
            × Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ZERO BALANCE CONFIRMATION MODAL
const ZeroBalanceConfirmModal = ({ client, financialData, onClose, onConfirm, processing }) => {
  const [confirmText, setConfirmText] = useState("");
  
  const canConfirm = confirmText.toUpperCase() === "ZERO";
  
  return (
    <div className="sirix-modal-overlay" data-testid="zero-balance-confirm" role="dialog" aria-label="Bakiye Sıfırlama Onayı" onClick={processing ? undefined : onClose}>
      <div className="sirix-modal" style={{ width: 480, maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="sirix-modal-header" style={{ background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}>
          <h2 className="sirix-modal-title text-white flex items-center gap-2">
            <Trash2 size={18} />
            Bakiye Sıfırlama Onayı
          </h2>
          <button className="sirix-modal-close text-white hover:text-white/80" data-testid="zero-balance-close" disabled={processing} onClick={onClose}>×</button>
        </div>
        
        {/* Account Info Bar */}
        <div className="bg-gray-100 px-6 py-3 border-b">
          <div className="text-sm text-gray-600">
            Hesap: <span className="font-mono font-medium">{client?.account_id || client?.email}</span>
            <span className="ml-4">{client?.full_name || client?.email}</span>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <Info size={20} className="text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-red-800 mb-1">Uyarı: Bu işlem geri alınamaz!</h4>
                <p className="text-sm text-red-700">
                  Bu hesabın bakiyesi ve kredisi kalıcı olarak sıfırlanacak.
                </p>
              </div>
            </div>
          </div>
          
          {/* Current Values */}
          <div className="bg-gray-50 rounded-lg p-4 border">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Güncel Hesap Değerleri:</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Bakiye:</span>
                <span className="font-mono font-bold text-lg">${(financialData?.balance || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Kredi:</span>
                <span className="font-mono font-bold text-lg">${(financialData?.credit || 0).toLocaleString()}</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t flex justify-between items-center">
              <span className="text-gray-600 font-medium">Sıfırlanacak Toplam:</span>
              <span className="font-mono font-bold text-xl text-red-600">
                ${((financialData?.balance || 0) + (financialData?.credit || 0)).toLocaleString()}
              </span>
            </div>
          </div>
          
          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Onaylamak için <span className="font-mono font-bold bg-gray-200 px-2 py-0.5 rounded">ZERO</span> yazın:
            </label>
            <input
              type="text"
              className={`sirix-form-input w-full py-2.5 font-mono text-center text-lg uppercase tracking-wider ${
                confirmText.length > 0 && !canConfirm ? 'border-red-300 bg-red-50' : ''
              } ${canConfirm ? 'border-green-300 bg-green-50' : ''}`}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="ZERO yazın"
              autoComplete="off"
              data-testid="zero-balance-confirm-input"
            />
          </div>
        </div>
        
        <div className="sirix-modal-footer bg-gray-50 px-6 py-4">
          <button 
            className={`sirix-btn px-6 py-2.5 text-white ${
              canConfirm && !processing
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-gray-400 cursor-not-allowed'
            }`}
            onClick={onConfirm}
            disabled={!canConfirm || processing}
            data-testid="zero-balance-submit"
          >
            {processing ? "İşleniyor…" : "Bakiyeyi Sıfırla"}
          </button>
          <button 
            className="sirix-btn sirix-btn-secondary px-6 py-2.5" 
            onClick={onClose}
            disabled={processing}
            data-testid="zero-balance-cancel"
          >
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
};

// OPEN POSITION MODAL - Create new position for client
const OpenPositionModal = ({ client, financialData, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    symbol: "XAUUSD",
    quantity: 1,
    side: "buy",
    priceType: "current", // "current" or "specific"
    specificPrice: 0,
    stopLoss: false,
    stopLossPrice: 0,
    takeProfit: false,
    takeProfitPrice: 0,
    sendToRisk: false,
    comment: ""
  });

  // Available instruments with their current prices
  const instruments = [
    { symbol: "XAUUSD", name: "Gold", flag: "🥇", bidPrice: 4680.49, askPrice: 4682.49, currency: "Oz." },
    { symbol: "EURUSD", name: "Euro/US Dollar", flag: "🇪🇺", bidPrice: 1.0874, askPrice: 1.0876, currency: "Lots" },
    { symbol: "GBPUSD", name: "British Pound/US Dollar", flag: "🇬🇧", bidPrice: 1.2650, askPrice: 1.2652, currency: "Lots" },
    { symbol: "USDJPY", name: "US Dollar/Japanese Yen", flag: "🇯🇵", bidPrice: 149.85, askPrice: 149.87, currency: "Lots" },
    { symbol: "BTCUSD", name: "Bitcoin", flag: "₿", bidPrice: 68150.00, askPrice: 68200.00, currency: "BTC" },
    { symbol: "ETHUSD", name: "Ethereum", flag: "Ξ", bidPrice: 3515.00, askPrice: 3520.00, currency: "ETH" },
    { symbol: "AAPL", name: "Apple Inc.", flag: "🍎", bidPrice: 178.20, askPrice: 178.25, currency: "Shares" },
    { symbol: "MSFT", name: "Microsoft Corp.", flag: "💻", bidPrice: 425.75, askPrice: 425.80, currency: "Shares" },
    { symbol: "NVDA", name: "NVIDIA Corp.", flag: "🎮", bidPrice: 895.40, askPrice: 895.50, currency: "Shares" },
    { symbol: "TSLA", name: "Tesla Inc.", flag: "🚗", bidPrice: 172.45, askPrice: 172.50, currency: "Shares" },
    { symbol: "THYAO.IS", name: "Turkish Airlines", flag: "🇹🇷", bidPrice: 292.25, askPrice: 292.30, currency: "Shares" },
    { symbol: "GARAN.IS", name: "Garanti Bank", flag: "🇹🇷", bidPrice: 128.35, askPrice: 128.40, currency: "Shares" },
    { symbol: "VERUS-IST", name: "Verus IST", flag: "🇹🇷", bidPrice: 395.95, askPrice: 396.00, currency: "$" },
    { symbol: "A1CAP-IST", name: "A1 Capital IST", flag: "🇹🇷", bidPrice: 15.72, askPrice: 15.75, currency: "$" },
  ];

  const selectedInstrument = instruments.find(i => i.symbol === formData.symbol) || instruments[0];
  const currentBid = selectedInstrument.bidPrice;
  const currentAsk = selectedInstrument.askPrice;

  const handleExecute = (side) => {
    const price = side === 'buy' ? currentAsk : currentBid;
    const executionPrice = formData.priceType === 'specific' ? formData.specificPrice : price;
    
    const newPosition = {
      id: `${Date.now()}`,
      symbol: formData.symbol,
      side: side,
      quantity: formData.quantity,
      units: selectedInstrument.currency,
      entry_price: executionPrice,
      current_price: price,
      opened_at: new Date().toLocaleString(),
      spread: Math.round((currentAsk - currentBid) * (formData.symbol.includes('USD') && !formData.symbol.includes('XAU') && !formData.symbol.includes('BTC') && !formData.symbol.includes('ETH') ? 10000 : 100)),
      swap: 0,
      commission: 0,
      leverage: 100,
      stop_loss: formData.stopLoss ? formData.stopLossPrice : null,
      take_profit: formData.takeProfit ? formData.takeProfitPrice : null,
      comment: formData.comment,
      source: "Broker"
    };
    
    onSave(newPosition);
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 650 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl text-[#00BCD4]">Open a position for account {client.id?.slice(0, 8) || client.email}</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>×</button>
        </div>
        
        {/* Info Bar */}
        <div className="bg-gray-100 px-6 py-4">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Account ID:</span>
              <span className="font-mono">{client.id?.slice(0, 8) || client.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Equity:</span>
              <span className="font-mono">${financialData?.equity?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Margin Level:</span>
              <span className={`font-mono ${(financialData?.marginLevel || 0) < 100 ? 'text-red-500' : 'text-green-500'}`}>
                {financialData?.marginLevel?.toFixed(2) || '0.00'}%
              </span>
            </div>
          </div>
        </div>

        {/* Position Section */}
        <div className="px-6 py-5">
          <div className="text-[#00BCD4] font-medium text-lg mb-6">Position</div>
          
          <div className="space-y-5">
            {/* Instrument Selection */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Instrument:</label>
              <select 
                className="sirix-form-select flex-1 py-2.5"
                value={formData.symbol}
                onChange={(e) => setFormData({...formData, symbol: e.target.value})}
              >
                {instruments.map(inst => (
                  <option key={inst.symbol} value={inst.symbol}>
                    {inst.flag} {inst.symbol} - {inst.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Amount */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Amount:</label>
              <input 
                type="number" 
                className="sirix-form-input flex-1 py-2.5"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                min="0.01"
                step="0.01"
              />
              <span className="text-sm text-gray-500 ml-2">{selectedInstrument.currency}</span>
            </div>

            {/* Price Type */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Price:</label>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input 
                    type="radio" 
                    name="priceType" 
                    checked={formData.priceType === 'current'}
                    onChange={() => setFormData({...formData, priceType: 'current'})}
                  />
                  <span className="text-sm">Current rate</span>
                </label>
                <label className="flex items-center gap-2">
                  <input 
                    type="radio" 
                    name="priceType" 
                    checked={formData.priceType === 'specific'}
                    onChange={() => setFormData({...formData, priceType: 'specific'})}
                  />
                  <span className="text-sm">Specific Rate</span>
                  {formData.priceType === 'specific' && (
                    <input 
                      type="number" 
                      className="sirix-form-input w-32 py-1"
                      value={formData.specificPrice}
                      onChange={(e) => setFormData({...formData, specificPrice: parseFloat(e.target.value) || 0})}
                      step="0.01"
                    />
                  )}
                </label>
              </div>
            </div>

            {/* Stop Loss */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Stop Loss:</label>
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={formData.stopLoss}
                  onChange={(e) => setFormData({...formData, stopLoss: e.target.checked})}
                  className="w-4 h-4"
                />
                {formData.stopLoss && (
                  <input 
                    type="number" 
                    className="sirix-form-input w-32 py-1"
                    value={formData.stopLossPrice}
                    onChange={(e) => setFormData({...formData, stopLossPrice: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    placeholder="Price"
                  />
                )}
              </div>
            </div>

            {/* Take Profit */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Take Profit:</label>
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  checked={formData.takeProfit}
                  onChange={(e) => setFormData({...formData, takeProfit: e.target.checked})}
                  className="w-4 h-4"
                />
                {formData.takeProfit && (
                  <input 
                    type="number" 
                    className="sirix-form-input w-32 py-1"
                    value={formData.takeProfitPrice}
                    onChange={(e) => setFormData({...formData, takeProfitPrice: parseFloat(e.target.value) || 0})}
                    step="0.01"
                    placeholder="Price"
                  />
                )}
              </div>
            </div>

            {/* Send to Risk */}
            <div className="flex items-center gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0">Send to Risk:</label>
              <input 
                type="checkbox" 
                checked={formData.sendToRisk}
                onChange={(e) => setFormData({...formData, sendToRisk: e.target.checked})}
                className="w-4 h-4"
              />
            </div>

            {/* Comment */}
            <div className="flex items-start gap-4">
              <label className="w-32 text-sm text-gray-700 shrink-0 pt-2">Comment:</label>
              <textarea 
                className="sirix-form-input flex-1 h-20 resize-y py-2"
                value={formData.comment}
                onChange={(e) => setFormData({...formData, comment: e.target.value})}
                placeholder="Optional comment..."
              />
            </div>
          </div>
        </div>

        {/* Market Execution */}
        <div className="px-6 py-4 border-t">
          <div className="text-[#00BCD4] font-medium mb-4">Market Execution</div>
          <div className="flex items-center gap-4">
            <button 
              className="flex-1 py-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium text-lg transition-colors"
              onClick={() => handleExecute('sell')}
            >
              <div className="text-xs opacity-80">SELL</div>
              <div className="text-xl font-bold">{currentBid.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </button>
            <button 
              className="flex-1 py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-lg transition-colors"
              onClick={() => handleExecute('buy')}
            >
              <div className="text-xs opacity-80">BUY</div>
              <div className="text-xl font-bold">{currentAsk.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center px-6 py-4 border-t bg-gray-50">
          <button 
            className="px-8 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded transition-colors font-medium"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// EDIT TRADING MODAL - Change trading terms and leverage for a client
const EditTradingModal = ({ client, settings, onClose, onSave, terms = TRADING_TERMS }) => {
  const [formData, setFormData] = useState({
    state: settings.state || "Active",
    tradingTerms: settings.tradingTerms || "ZG Default TRY",
    marginRequirements: settings.marginRequirements || "Trading Terms",
    customLeverage: settings.customLeverage || 100,
    tradability: settings.tradability || "Full Trade"
  });

  // Get default leverage from selected trading term
  const selectedTermLeverage = terms.find(t => t.name === formData.tradingTerms)?.defaultLeverage || 100;

  const [saving,setSaving] = useState(false);
  const handleSave = async () => {
    if(saving)return;
    setSaving(true);
    try { await onSave(formData); } finally { setSaving(false); }
  };

  return (
    <div className="sirix-modal-overlay" onClick={onClose}>
      <div className="sirix-modal" style={{ width: 550 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl text-[#00BCD4]" data-testid="account-edit-trading-title">Hesap İşlem Ayarları — {client.account_id || client.email}</h2>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>×</button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-5">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-gray-700">Durum:</label>
            <select 
              className="sirix-form-select flex-1"
              value={formData.state}
              onChange={(e) => setFormData({...formData, state: e.target.value})}
            >
              <option value="Active">Aktif</option>
              <option value="Inactive">Pasif</option>
              <option value="Read Only">Salt Okunur</option>
            </select>
          </div>

          {/* Trading Terms */}
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-gray-700">İşlem Koşulları:</label>
            <select 
              className="sirix-form-select flex-1"
              data-testid="account-trading-term"
              value={formData.tradingTerms}
              onChange={(e) => {
                const newTerm = terms.find(t => t.name === e.target.value);
                setFormData({
                  ...formData, 
                  tradingTerms: e.target.value,
                  // If using Trading Terms leverage, update to the new term's default
                  marginRequirements: "Trading Terms",
                  customLeverage: newTerm?.defaultLeverage || 100
                });
              }}
            >
              {terms.map(term => (
                <option key={term.id} value={term.name}>{term.name}</option>
              ))}
            </select>
          </div>

          {/* Margin Requirements */}
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-gray-700">Kaldıraç Kaynağı:</label>
            <select 
              className="sirix-form-select flex-1"
              data-testid="account-leverage-source"
              value={formData.marginRequirements}
              onChange={(e) => setFormData({
                ...formData, 
                marginRequirements: e.target.value,
                // Reset to term leverage if switching to Trading Terms
                customLeverage: e.target.value === "Trading Terms" ? selectedTermLeverage : formData.customLeverage
              })}
            >
              <option value="Trading Terms">İşlem Koşulu</option>
              <option value="Custom">Hesaba Özel</option>
            </select>
          </div>

          {/* Custom Leverage (only if Custom is selected) */}
          {formData.marginRequirements === "Custom" && (
            <div className="flex items-center gap-4">
              <label className="w-40 text-sm text-gray-700">Kaldıraç:</label>
              <div className="flex-1">
                <LeverageSelector 
                  value={formData.customLeverage} 
                  testId="account-leverage"
                  onChange={(val) => setFormData({...formData, customLeverage: val})}
                  showInfo={true}
                />
              </div>
            </div>
          )}

          {/* Show current leverage info */}
          {formData.marginRequirements === "Trading Terms" && (
            <div className="flex items-center gap-4 bg-blue-50 p-3 rounded">
              <Info size={16} className="text-blue-500" />
              <span className="text-sm text-blue-700">
                İşlem koşulunun kaldıracı: <strong>1:{selectedTermLeverage}</strong>
              </span>
            </div>
          )}

          <p className="text-xs text-amber-600" data-testid="account-leverage-warning">Kaldıraç değişikliği açık pozisyonların teminatını hemen etkiler ve zorunlu kapatma tetikleyebilir.</p>
          {/* Tradability */}
          <div className="flex items-center gap-4">
            <label className="w-40 text-sm text-gray-700">İşlem Yetkisi:</label>
            <select 
              className="sirix-form-select flex-1"
              value={formData.tradability}
              onChange={(e) => setFormData({...formData, tradability: e.target.value})}
            >
              <option value="Full Trade">Tam İşlem</option>
              <option value="Close Only">Yalnızca Kapat</option>
              <option value="Disabled">Kapalı</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-4 px-6 py-4 border-t bg-gray-50">
          <button 
            className="px-8 py-2.5 bg-[#00BCD4] hover:bg-[#00ACC1] text-white rounded transition-colors font-medium"
            onClick={handleSave}
            disabled={saving}
            data-testid="account-edit-trading-save"
          >
            ✓ Kaydet
          </button>
          <button 
            className="px-8 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded transition-colors font-medium"
            onClick={onClose}
          >
            ✗ Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
};

export { TradingTab, PersonalDetailsTab, EditTradingModal, EditPositionModal, BulkEditModal, EditClosedPositionModal, ZeroBalanceConfirmModal };
export default AccountDetailModal;
